import { useState, useEffect, useRef } from 'react';
import { 
  Lock, Package, ShoppingCart, Users, BarChart3, Mail, 
  AlertTriangle, Download, Trash2, Edit, Plus, X,
  ChevronDown, ChevronUp, Save, Store, MapPin, ExternalLink, Upload, Loader2, Bell, BellOff, Heart, Camera, Tag, Percent, DollarSign, Calendar, Hammer, Clock, CheckCircle, Truck as TruckIcon, Link2, QrCode, Copy, Check, FolderOpen
} from 'lucide-react';
import { api } from '../lib/api';

const AdminPage = () => {
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [password, setPassword] = useState('');
  const [storedPassword, setStoredPassword] = useState('');
  const [loginError, setLoginError] = useState('');
  const [activeTab, setActiveTab] = useState('dashboard');
  const [stats, setStats] = useState(null);
  const [orders, setOrders] = useState([]);
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [artists, setArtists] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [marketplaceProducts, setMarketplaceProducts] = useState([]);
  const [subscribers, setSubscribers] = useState([]);
  const [newSubscriberCount, setNewSubscriberCount] = useState(0);
  const [galleryPhotos, setGalleryPhotos] = useState([]);
  const [discountCodes, setDiscountCodes] = useState([]);
  const [discountStats, setDiscountStats] = useState(null);
  const [btoOrders, setBtoOrders] = useState([]);
  const [btoStats, setBtoStats] = useState(null);
  const [expandedBTOOrder, setExpandedBTOOrder] = useState(null);
  const [copiedLink, setCopiedLink] = useState(null);
  const [loading, setLoading] = useState(false);
  const [expandedOrder, setExpandedOrder] = useState(null);
  const [expandedArtist, setExpandedArtist] = useState(null);
  const [notificationEnabled, setNotificationEnabled] = useState(true);
  
  // Product form state
  const [showProductModal, setShowProductModal] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [productForm, setProductForm] = useState({
    title: '', price: '', description: '', dimensions: '', materials: '',
    care_instructions: '', images: [{ url: '', alt: '' }], category_id: '',
    stock: 1, featured: false, weight: '',
    ship_weight: 2, ship_length: 12, ship_width: 10, ship_height: 8
  });

  // Marketplace product form state
  const [showMarketplaceModal, setShowMarketplaceModal] = useState(false);
  const [editingMarketplaceProduct, setEditingMarketplaceProduct] = useState(null);
  const [marketplaceForm, setMarketplaceForm] = useState({
    title: '', price: '', description: '', dimensions: '', materials: '',
    weight: '', intended_use: '', shipping_info: '', images: [{ url: '', alt: '' }],
    artist_id: '', stock: 1,
    ship_weight: 2, ship_length: 12, ship_width: 10, ship_height: 8
  });

  // Artist form state
  const [showArtistModal, setShowArtistModal] = useState(false);
  const [editingArtist, setEditingArtist] = useState(null);
  const [artistForm, setArtistForm] = useState({
    name: '',
    legal_name: '',
    title: '',
    description: '',
    powers: [{ name: '', description: '' }],
    weakness: '',
    card_image: '',
    website_url: '',
    city: '',
    state: '',
    // Shipping address (private - for Shippo calculations)
    ship_street: '',
    ship_city: '',
    ship_state: '',
    ship_zip: ''
  });

  // Gallery form state
  const [showGalleryModal, setShowGalleryModal] = useState(false);
  const [editingGalleryPhoto, setEditingGalleryPhoto] = useState(null);
  const [galleryForm, setGalleryForm] = useState({
    url: '',
    caption: ''
  });
  // Inline editing state for gallery
  const [inlineEditingPhotoId, setInlineEditingPhotoId] = useState(null);
  const [inlineEditCaption, setInlineEditCaption] = useState('');

  // Discount form state
  const [showDiscountModal, setShowDiscountModal] = useState(false);
  const [editingDiscount, setEditingDiscount] = useState(null);
  const [discountForm, setDiscountForm] = useState({
    code: '',
    discount_type: 'percentage',
    discount_value: '',
    usage_limit: '',
    expires_at: '',
    is_active: true
  });

  // Category form state
  const [showCategoryModal, setShowCategoryModal] = useState(false);
  const [categoryForm, setCategoryForm] = useState({
    name: '',
    slug: '',
    description: ''
  });

  // Upload state
  const [uploading, setUploading] = useState(false);

  // Image upload handler
  const handleImageUpload = async (file, onSuccess) => {
    if (!file) return;
    setUploading(true);
    try {
      const result = await api.uploadImage(file);
      const fullUrl = `${process.env.REACT_APP_BACKEND_URL}${result.url}`;
      onSuccess(fullUrl);
    } catch (error) {
      console.error('Upload failed:', error);
      alert('Failed to upload image. Please try again.');
    } finally {
      setUploading(false);
    }
  };

  // Simple encoding for localStorage (not encryption, but better than plaintext)
  const encodeAuth = (str) => btoa(encodeURIComponent(str));
  const decodeAuth = (str) => {
    try {
      return decodeURIComponent(atob(str));
    } catch {
      return null;
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoginError('');
    try {
      await api.adminLogin(password);
      setIsLoggedIn(true);
      setStoredPassword(password);
      // Store encoded password with timestamp for session management
      const authData = JSON.stringify({ 
        auth: encodeAuth(password), 
        timestamp: Date.now() 
      });
      localStorage.setItem('adminAuth', authData);
    } catch (error) {
      setLoginError('Invalid password');
    }
  };

  useEffect(() => {
    const savedAuthStr = localStorage.getItem('adminAuth');
    if (savedAuthStr) {
      try {
        const { auth, timestamp } = JSON.parse(savedAuthStr);
        // Session expires after 24 hours
        const SESSION_DURATION = 24 * 60 * 60 * 1000;
        if (Date.now() - timestamp < SESSION_DURATION) {
          const decodedAuth = decodeAuth(auth);
          if (decodedAuth) {
            setStoredPassword(decodedAuth);
            setIsLoggedIn(true);
          }
        } else {
          localStorage.removeItem('adminAuth');
        }
      } catch {
        localStorage.removeItem('adminAuth');
      }
    }
    // decodeAuth is a stable function defined in component scope
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (isLoggedIn && storedPassword) {
      fetchDashboardData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoggedIn, storedPassword, activeTab]);

  const fetchDashboardData = async () => {
    setLoading(true);
    try {
      const catsData = await api.getCategories();
      setCategories(catsData);
      const artistsData = await api.adminGetArtists(storedPassword);
      setArtists(artistsData);
      
      if (activeTab === 'dashboard') {
        const statsData = await api.adminGetStats(storedPassword);
        setStats(statsData);
        // Fetch notification setting
        const notifSetting = await api.getPurchaseNotificationSetting();
        setNotificationEnabled(notifSetting.enabled);
      } else if (activeTab === 'orders') {
        const ordersData = await api.adminGetOrders(storedPassword);
        setOrders(ordersData);
      } else if (activeTab === 'products') {
        const productsData = await api.adminGetProducts(storedPassword);
        setProducts(productsData);
      } else if (activeTab === 'marketplace') {
        const mpData = await api.adminGetMarketplaceProducts(storedPassword);
        setMarketplaceProducts(mpData);
      } else if (activeTab === 'messages') {
        const contactsData = await api.adminGetContacts(storedPassword);
        setContacts(contactsData);
      } else if (activeTab === 'subscribers') {
        const subscribersData = await api.getSubscribers();
        setSubscribers(subscribersData);
        // Mark as seen when viewing
        await api.markSubscribersSeen();
        setNewSubscriberCount(0);
      } else if (activeTab === 'gallery') {
        const galleryData = await api.getGalleryPhotos();
        setGalleryPhotos(galleryData);
      } else if (activeTab === 'discounts') {
        const discountsData = await api.getDiscountCodes(storedPassword);
        setDiscountCodes(discountsData);
        const discountStatsData = await api.getDiscountStats(storedPassword);
        setDiscountStats(discountStatsData);
      } else if (activeTab === 'bto') {
        const btoData = await api.getBTOOrders();
        setBtoOrders(btoData);
        const btoStatsData = await api.getBTOStats();
        setBtoStats(btoStatsData);
      }
      
      // Always fetch new subscriber count for badge
      const subCount = await api.getSubscriberCount();
      setNewSubscriberCount(subCount.new);
    } catch (error) {
      console.error('Error fetching data:', error);
      if (error.response?.status === 401) {
        setIsLoggedIn(false);
        localStorage.removeItem('adminAuth');
      }
    }
    setLoading(false);
  };

  const handleLogout = () => {
    setIsLoggedIn(false);
    setStoredPassword('');
    setPassword('');
    localStorage.removeItem('adminAuth');
  };

  const toggleNotificationSetting = async () => {
    try {
      const newValue = !notificationEnabled;
      await api.setPurchaseNotificationSetting(newValue);
      setNotificationEnabled(newValue);
    } catch (error) {
      console.error('Error toggling notification:', error);
    }
  };

  const updateOrderStatus = async (orderId, newStatus) => {
    try {
      await api.adminUpdateOrderStatus(orderId, newStatus, storedPassword);
      fetchDashboardData();
    } catch (error) {
      console.error('Error updating order:', error);
    }
  };

  const deleteProduct = async (productId) => {
    if (window.confirm('Are you sure you want to delete this product?')) {
      try {
        await api.adminDeleteProduct(productId, storedPassword);
        fetchDashboardData();
      } catch (error) {
        console.error('Error deleting product:', error);
      }
    }
  };

  const deleteArtist = async (artistId) => {
    if (window.confirm('Are you sure you want to delete this artist? This will also affect their marketplace products.')) {
      try {
        await api.adminDeleteArtist(artistId, storedPassword);
        fetchDashboardData();
      } catch (error) {
        console.error('Error deleting artist:', error);
      }
    }
  };

  const deleteContact = async (contactId) => {
    try {
      await api.adminDeleteContact(contactId, storedPassword);
      fetchDashboardData();
    } catch (error) {
      console.error('Error deleting contact:', error);
    }
  };

  const exportOrders = async () => {
    try {
      const data = await api.adminExportOrders(storedPassword);
      const blob = new Blob([data.csv], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `orders_${new Date().toISOString().split('T')[0]}.csv`;
      a.click();
    } catch (error) {
      console.error('Error exporting orders:', error);
    }
  };

  // Product form handlers
  const openAddProduct = () => {
    setEditingProduct(null);
    setProductForm({
      title: '', price: '', description: '', dimensions: '', materials: '',
      care_instructions: '', images: [{ url: '', alt: '' }],
      category_id: categories[0]?.id || '', stock: 1, featured: false, weight: '',
      ship_weight: 2, ship_length: 12, ship_width: 10, ship_height: 8
    });
    setShowProductModal(true);
  };

  const openEditProduct = (product) => {
    setEditingProduct(product);
    setProductForm({
      title: product.title || '', price: product.price || '',
      description: product.description || '', dimensions: product.dimensions || '',
      materials: product.materials || '', care_instructions: product.care_instructions || '',
      images: product.images?.length > 0 ? product.images : [{ url: '', alt: '' }],
      category_id: product.category_id || '', stock: product.stock || 1,
      featured: product.featured || false, weight: product.weight || '',
      ship_weight: product.ship_weight || 2, ship_length: product.ship_length || 12,
      ship_width: product.ship_width || 10, ship_height: product.ship_height || 8
    });
    setShowProductModal(true);
  };

  const handleProductFormChange = (field, value) => {
    setProductForm(prev => ({ ...prev, [field]: value }));
  };

  const handleImageChange = (index, field, value) => {
    const newImages = [...productForm.images];
    newImages[index] = { ...newImages[index], [field]: value };
    setProductForm(prev => ({ ...prev, images: newImages }));
  };

  const addImageField = () => {
    setProductForm(prev => ({ ...prev, images: [...prev.images, { url: '', alt: '' }] }));
  };

  const removeImageField = (index) => {
    if (productForm.images.length > 1) {
      setProductForm(prev => ({ ...prev, images: prev.images.filter((_, i) => i !== index) }));
    }
  };

  const saveProduct = async () => {
    try {
      const productData = {
        ...productForm,
        price: parseFloat(productForm.price) || 0,
        stock: parseInt(productForm.stock) || 1,
        ship_weight: parseFloat(productForm.ship_weight) || 2,
        ship_length: parseFloat(productForm.ship_length) || 12,
        ship_width: parseFloat(productForm.ship_width) || 10,
        ship_height: parseFloat(productForm.ship_height) || 8,
        images: productForm.images.filter(img => img.url)
      };
      if (editingProduct) {
        await api.adminUpdateProduct(editingProduct.id, productData, storedPassword);
      } else {
        await api.adminCreateProduct(productData, storedPassword);
      }
      setShowProductModal(false);
      fetchDashboardData();
    } catch (error) {
      console.error('Error saving product:', error);
      alert('Error saving product. Please check all fields.');
    }
  };

  // Debounce refs for stock updates
  const stockUpdateTimeouts = useRef({});
  
  const quickUpdateStock = async (productId, newStock) => {
    // Update local state immediately (optimistic update) - prevents scroll
    setProducts(prev => prev.map(p => 
      p.id === productId ? { ...p, stock: parseInt(newStock) || 0 } : p
    ));
    
    // Clear existing timeout for this product
    if (stockUpdateTimeouts.current[productId]) {
      clearTimeout(stockUpdateTimeouts.current[productId]);
    }
    
    // Debounce API call - only call after user stops typing for 800ms
    stockUpdateTimeouts.current[productId] = setTimeout(async () => {
      const product = products.find(p => p.id === productId);
      if (product) {
        try {
          await api.adminUpdateProduct(productId, { ...product, stock: parseInt(newStock) || 0 }, storedPassword);
        } catch (error) {
          console.error('Error updating stock:', error);
        }
      }
    }, 800);
  };

  // Marketplace product handlers
  const openAddMarketplaceProduct = () => {
    setEditingMarketplaceProduct(null);
    setMarketplaceForm({
      title: '', price: '', description: '', dimensions: '', materials: '',
      weight: '', intended_use: '', shipping_info: '', images: [{ url: '', alt: '' }],
      artist_id: artists[0]?.id || '', stock: 1,
      ship_weight: 2, ship_length: 12, ship_width: 10, ship_height: 8
    });
    setShowMarketplaceModal(true);
  };

  const openEditMarketplaceProduct = (product) => {
    setEditingMarketplaceProduct(product);
    setMarketplaceForm({
      title: product.title || '', price: product.price || '',
      description: product.description || '', dimensions: product.dimensions || '',
      materials: product.materials || '', weight: product.weight || '',
      intended_use: product.intended_use || '', shipping_info: product.shipping_info || '',
      images: product.images?.length > 0 ? product.images : [{ url: '', alt: '' }],
      artist_id: product.artist_id || '', stock: product.stock || 1,
      ship_weight: product.ship_weight || 2, ship_length: product.ship_length || 12,
      ship_width: product.ship_width || 10, ship_height: product.ship_height || 8
    });
    setShowMarketplaceModal(true);
  };

  const handleMarketplaceFormChange = (field, value) => {
    setMarketplaceForm(prev => ({ ...prev, [field]: value }));
  };

  const handleMarketplaceImageChange = (index, field, value) => {
    const newImages = [...marketplaceForm.images];
    newImages[index] = { ...newImages[index], [field]: value };
    setMarketplaceForm(prev => ({ ...prev, images: newImages }));
  };

  const addMarketplaceImageField = () => {
    setMarketplaceForm(prev => ({ ...prev, images: [...prev.images, { url: '', alt: '' }] }));
  };

  const removeMarketplaceImageField = (index) => {
    if (marketplaceForm.images.length > 1) {
      setMarketplaceForm(prev => ({ ...prev, images: prev.images.filter((_, i) => i !== index) }));
    }
  };

  const saveMarketplaceProduct = async () => {
    try {
      const productData = {
        ...marketplaceForm,
        price: parseFloat(marketplaceForm.price) || 0,
        stock: parseInt(marketplaceForm.stock) || 1,
        ship_weight: parseFloat(marketplaceForm.ship_weight) || 2,
        ship_length: parseFloat(marketplaceForm.ship_length) || 12,
        ship_width: parseFloat(marketplaceForm.ship_width) || 10,
        ship_height: parseFloat(marketplaceForm.ship_height) || 8,
        images: marketplaceForm.images.filter(img => img.url)
      };
      if (editingMarketplaceProduct) {
        await api.adminUpdateMarketplaceProduct(editingMarketplaceProduct.id, productData, storedPassword);
      } else {
        await api.adminCreateMarketplaceProduct(productData, storedPassword);
      }
      setShowMarketplaceModal(false);
      fetchDashboardData();
    } catch (error) {
      console.error('Error saving marketplace product:', error);
      alert('Error saving product. Please check all fields and make sure an artist is selected.');
    }
  };

  const deleteMarketplaceProduct = async (productId) => {
    if (window.confirm('Are you sure you want to delete this marketplace product?')) {
      try {
        await api.adminDeleteMarketplaceProduct(productId, storedPassword);
        fetchDashboardData();
      } catch (error) {
        console.error('Error deleting marketplace product:', error);
      }
    }
  };

  // Debounce ref for marketplace stock updates
  const marketplaceStockUpdateTimeouts = useRef({});
  
  const quickUpdateMarketplaceStock = async (productId, newStock) => {
    // Update local state immediately (optimistic update) - prevents scroll
    setMarketplaceProducts(prev => prev.map(p => 
      p.id === productId ? { ...p, stock: parseInt(newStock) || 0 } : p
    ));
    
    // Clear existing timeout for this product
    if (marketplaceStockUpdateTimeouts.current[productId]) {
      clearTimeout(marketplaceStockUpdateTimeouts.current[productId]);
    }
    
    // Debounce API call - only call after user stops typing for 800ms
    marketplaceStockUpdateTimeouts.current[productId] = setTimeout(async () => {
      const product = marketplaceProducts.find(p => p.id === productId);
      if (product) {
        try {
          await api.adminUpdateMarketplaceProduct(productId, { ...product, stock: parseInt(newStock) || 0 }, storedPassword);
        } catch (error) {
          console.error('Error updating stock:', error);
        }
      }
    }, 800);
  };

  const getArtistName = (artistId) => {
    const artist = artists.find(a => a.id === artistId);
    return artist?.name || 'Unknown Artist';
  };

  // Artist form handlers
  const openAddArtist = () => {
    setEditingArtist(null);
    setArtistForm({
      name: '', legal_name: '', title: '', description: '',
      powers: [{ name: '', description: '' }], weakness: '',
      card_image: '', website_url: '', city: '', state: '',
      ship_street: '', ship_city: '', ship_state: '', ship_zip: ''
    });
    setShowArtistModal(true);
  };

  const openEditArtist = (artist) => {
    setEditingArtist(artist);
    setArtistForm({
      name: artist.name || '',
      legal_name: artist.legal_name || '',
      title: artist.title || '',
      description: artist.description || '',
      powers: artist.powers?.length > 0 ? artist.powers : [{ name: '', description: '' }],
      weakness: artist.weakness || '',
      card_image: artist.card_image || '',
      website_url: artist.website_url || '',
      city: artist.city || '',
      state: artist.state || '',
      ship_street: artist.ship_street || '',
      ship_city: artist.ship_city || '',
      ship_state: artist.ship_state || '',
      ship_zip: artist.ship_zip || ''
    });
    setShowArtistModal(true);
  };

  const handleArtistFormChange = (field, value) => {
    setArtistForm(prev => ({ ...prev, [field]: value }));
  };

  const handlePowerChange = (index, field, value) => {
    const newPowers = [...artistForm.powers];
    newPowers[index] = { ...newPowers[index], [field]: value };
    setArtistForm(prev => ({ ...prev, powers: newPowers }));
  };

  const addPowerField = () => {
    setArtistForm(prev => ({ ...prev, powers: [...prev.powers, { name: '', description: '' }] }));
  };

  const removePowerField = (index) => {
    if (artistForm.powers.length > 1) {
      setArtistForm(prev => ({ ...prev, powers: prev.powers.filter((_, i) => i !== index) }));
    }
  };

  const saveArtist = async () => {
    try {
      const artistData = {
        ...artistForm,
        powers: artistForm.powers.filter(p => p.name)
      };
      if (editingArtist) {
        await api.adminUpdateArtist(editingArtist.id, artistData, storedPassword);
      } else {
        await api.adminCreateArtist(artistData, storedPassword);
      }
      setShowArtistModal(false);
      fetchDashboardData();
    } catch (error) {
      console.error('Error saving artist:', error);
      alert('Error saving artist. Please check all required fields.');
    }
  };

  // Login Screen
  if (!isLoggedIn) {
    return (
      <div className="pt-20 min-h-screen bg-void flex items-center justify-center px-4">
        <div className="w-full max-w-md">
          <div className="bg-void-paper border border-white/10 p-8">
            <div className="text-center mb-8">
              <div className="w-16 h-16 bg-cyan-400/20 rounded-full flex items-center justify-center mx-auto mb-4">
                <Lock className="w-8 h-8 text-cyan-400" />
              </div>
              <h1 className="font-heading text-2xl font-bold text-white">Admin Dashboard</h1>
              <p className="text-neutral-400 text-sm mt-2">Enter password to access</p>
            </div>
            <form onSubmit={handleLogin}>
              <input type="password" value={password} onChange={(e) => setPassword(e.target.value)}
                placeholder="Password" data-testid="admin-password-input"
                className="w-full bg-void border border-white/20 px-4 py-3 text-white placeholder-neutral-500 focus:border-cyan-400 focus:outline-none mb-4" />
              {loginError && <p className="text-red-400 text-sm mb-4">{loginError}</p>}
              <button type="submit" data-testid="admin-login-btn"
                className="w-full bg-cyan-400 text-black py-3 font-mono uppercase tracking-widest text-sm hover:bg-cyan-300 transition-all">
                Login
              </button>
            </form>
          </div>
        </div>
      </div>
    );
  }

  // Product Modal
  const renderProductModal = () => showProductModal && (
    <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4 overflow-y-auto">
      <div className="bg-void-paper border border-white/10 w-full max-w-2xl my-8">
        <div className="flex items-center justify-between p-4 border-b border-white/10">
          <h2 className="font-heading text-xl font-bold text-white">{editingProduct ? 'Edit Product' : 'Add New Product'}</h2>
          <button onClick={() => setShowProductModal(false)} className="text-neutral-400 hover:text-white"><X className="w-6 h-6" /></button>
        </div>
        <div className="p-4 space-y-4 max-h-[70vh] overflow-y-auto">
          <div><label className="block text-neutral-400 text-sm mb-1">Title *</label>
            <input type="text" value={productForm.title} onChange={(e) => handleProductFormChange('title', e.target.value)}
              className="w-full bg-void border border-white/20 px-3 py-2 text-white focus:border-cyan-400 focus:outline-none" /></div>
          <div className="grid grid-cols-2 gap-4">
            <div><label className="block text-neutral-400 text-sm mb-1">Price *</label>
              <input type="number" step="0.01" value={productForm.price} onChange={(e) => handleProductFormChange('price', e.target.value)}
                className="w-full bg-void border border-white/20 px-3 py-2 text-white focus:border-cyan-400 focus:outline-none" /></div>
            <div><label className="block text-neutral-400 text-sm mb-1">Stock *</label>
              <input type="number" value={productForm.stock} onChange={(e) => handleProductFormChange('stock', e.target.value)}
                className="w-full bg-void border border-white/20 px-3 py-2 text-white focus:border-cyan-400 focus:outline-none" /></div>
          </div>
          <div><label className="block text-neutral-400 text-sm mb-1">Category</label>
            <select value={productForm.category_id} onChange={(e) => handleProductFormChange('category_id', e.target.value)}
              className="w-full bg-void border border-white/20 px-3 py-2 text-white focus:border-cyan-400 focus:outline-none">
              <option value="">Select category</option>
              {categories.map(cat => <option key={cat.id} value={cat.id}>{cat.name}{cat.no_shipping ? ' (No Shipping)' : ''}</option>)}
            </select>
            {categories.find(c => c.id === productForm.category_id)?.no_shipping && (
              <p className="text-amber-400 text-xs mt-1">⚠️ This category is marked as "No Shipping Required" - items are add-ons only</p>
            )}
          </div>
          <div><label className="block text-neutral-400 text-sm mb-1">Description</label>
            <textarea value={productForm.description} onChange={(e) => handleProductFormChange('description', e.target.value)} rows={3}
              className="w-full bg-void border border-white/20 px-3 py-2 text-white focus:border-cyan-400 focus:outline-none resize-none"></textarea></div>
          <div className="grid grid-cols-3 gap-4">
            <div><label className="block text-neutral-400 text-sm mb-1">Product Dimensions</label>
              <input type="text" value={productForm.dimensions} onChange={(e) => handleProductFormChange('dimensions', e.target.value)}
                placeholder="e.g., 6x4x5 inches"
                className="w-full bg-void border border-white/20 px-3 py-2 text-white focus:border-cyan-400 focus:outline-none" /></div>
            <div><label className="block text-neutral-400 text-sm mb-1">Materials</label>
              <input type="text" value={productForm.materials} onChange={(e) => handleProductFormChange('materials', e.target.value)}
                className="w-full bg-void border border-white/20 px-3 py-2 text-white focus:border-cyan-400 focus:outline-none" /></div>
            <div><label className="block text-neutral-400 text-sm mb-1">Display Weight</label>
              <input type="text" value={productForm.weight} onChange={(e) => handleProductFormChange('weight', e.target.value)}
                placeholder="e.g., 2 lbs"
                className="w-full bg-void border border-white/20 px-3 py-2 text-white focus:border-cyan-400 focus:outline-none" /></div>
          </div>
          
          {/* Shipping Box Dimensions for Shippo */}
          <div className="bg-cyan-400/5 border border-cyan-400/20 p-4 rounded">
            <div className="flex items-center justify-between mb-3">
              <h4 className="text-cyan-400 font-mono text-xs uppercase tracking-widest">Shipping Box Dimensions (for accurate quotes)</h4>
              <button
                type="button"
                onClick={() => {
                  handleProductFormChange('ship_weight', '0.5');
                  handleProductFormChange('ship_length', '10');
                  handleProductFormChange('ship_width', '8');
                  handleProductFormChange('ship_height', '1');
                }}
                className="px-3 py-1 bg-purple-500/20 border border-purple-500/40 text-purple-400 text-xs font-mono uppercase hover:bg-purple-500/30 transition-all"
              >
                Plastic Bag
              </button>
            </div>
            <div className="grid grid-cols-4 gap-3">
              <div><label className="block text-neutral-400 text-xs mb-1">Weight (lbs)</label>
                <input type="number" step="0.1" value={productForm.ship_weight} onChange={(e) => handleProductFormChange('ship_weight', e.target.value)}
                  className="w-full bg-void border border-white/20 px-3 py-2 text-white focus:border-cyan-400 focus:outline-none text-sm" /></div>
              <div><label className="block text-neutral-400 text-xs mb-1">Length (in)</label>
                <input type="number" step="0.5" value={productForm.ship_length} onChange={(e) => handleProductFormChange('ship_length', e.target.value)}
                  className="w-full bg-void border border-white/20 px-3 py-2 text-white focus:border-cyan-400 focus:outline-none text-sm" /></div>
              <div><label className="block text-neutral-400 text-xs mb-1">Width (in)</label>
                <input type="number" step="0.5" value={productForm.ship_width} onChange={(e) => handleProductFormChange('ship_width', e.target.value)}
                  className="w-full bg-void border border-white/20 px-3 py-2 text-white focus:border-cyan-400 focus:outline-none text-sm" /></div>
              <div><label className="block text-neutral-400 text-xs mb-1">Height (in)</label>
                <input type="number" step="0.5" value={productForm.ship_height} onChange={(e) => handleProductFormChange('ship_height', e.target.value)}
                  className="w-full bg-void border border-white/20 px-3 py-2 text-white focus:border-cyan-400 focus:outline-none text-sm" /></div>
            </div>
          </div>
          
          <div><label className="block text-neutral-400 text-sm mb-1">Care Instructions</label>
            <textarea value={productForm.care_instructions} onChange={(e) => handleProductFormChange('care_instructions', e.target.value)} rows={2}
              className="w-full bg-void border border-white/20 px-3 py-2 text-white focus:border-cyan-400 focus:outline-none resize-none"></textarea></div>
          <div><label className="block text-neutral-400 text-sm mb-2">Images</label>
            {productForm.images.map((img, index) => (
              <div key={`product-img-${index}`} className="flex gap-2 mb-2 items-center">
                <input type="text" placeholder="Image URL" value={img.url} onChange={(e) => handleImageChange(index, 'url', e.target.value)}
                  className="flex-1 bg-void border border-white/20 px-3 py-2 text-white focus:border-cyan-400 focus:outline-none text-sm" />
                <input type="text" placeholder="Alt text" value={img.alt} onChange={(e) => handleImageChange(index, 'alt', e.target.value)}
                  className="w-32 bg-void border border-white/20 px-3 py-2 text-white focus:border-cyan-400 focus:outline-none text-sm" />
                <label className="px-3 py-2 bg-cyan-400/20 border border-cyan-400/40 text-cyan-400 hover:bg-cyan-400/30 cursor-pointer transition-all flex items-center gap-1">
                  {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
                  <input type="file" accept="image/*" className="hidden" onChange={(e) => {
                    const file = e.target.files?.[0];
                    if (file) handleImageUpload(file, (url) => handleImageChange(index, 'url', url));
                    e.target.value = '';
                  }} />
                </label>
                {productForm.images.length > 1 && <button onClick={() => removeImageField(index)} className="px-2 text-red-400 hover:bg-red-500/20"><X className="w-4 h-4" /></button>}
              </div>))}
            <button type="button" onClick={addImageField} className="text-cyan-400 text-sm hover:text-cyan-300 flex items-center gap-1"><Plus className="w-4 h-4" /> Add another image</button></div>
          <div className="flex items-center gap-2">
            <input type="checkbox" id="featured" checked={productForm.featured} onChange={(e) => handleProductFormChange('featured', e.target.checked)} className="w-4 h-4" />
            <label htmlFor="featured" className="text-neutral-400 text-sm">Featured product</label></div>
        </div>
        <div className="p-4 border-t border-white/10 flex justify-end gap-3">
          <button onClick={() => setShowProductModal(false)} className="px-4 py-2 border border-white/20 text-neutral-400 hover:text-white transition-all">Cancel</button>
          <button onClick={saveProduct} className="px-4 py-2 bg-cyan-400 text-black font-mono uppercase tracking-widest text-sm hover:bg-cyan-300 transition-all flex items-center gap-2">
            <Save className="w-4 h-4" />{editingProduct ? 'Update' : 'Create'}</button>
        </div>
      </div>
    </div>
  );

  // Marketplace Product Modal
  const renderMarketplaceModal = () => showMarketplaceModal && (
    <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4 overflow-y-auto">
      <div className="bg-void-paper border border-white/10 w-full max-w-2xl my-8">
        <div className="flex items-center justify-between p-4 border-b border-white/10">
          <h2 className="font-heading text-xl font-bold text-white">{editingMarketplaceProduct ? 'Edit Marketplace Product' : 'Add Marketplace Product'}</h2>
          <button onClick={() => setShowMarketplaceModal(false)} className="text-neutral-400 hover:text-white"><X className="w-6 h-6" /></button>
        </div>
        <div className="p-4 space-y-4 max-h-[70vh] overflow-y-auto">
          <div><label className="block text-neutral-400 text-sm mb-1">Artist *</label>
            <select value={marketplaceForm.artist_id} onChange={(e) => handleMarketplaceFormChange('artist_id', e.target.value)}
              className="w-full bg-void border border-white/20 px-3 py-2 text-white focus:border-cyan-400 focus:outline-none">
              <option value="">Select artist</option>
              {artists.map(artist => <option key={artist.id} value={artist.id}>{artist.name} - {artist.title}</option>)}
            </select></div>
          <div><label className="block text-neutral-400 text-sm mb-1">Title *</label>
            <input type="text" value={marketplaceForm.title} onChange={(e) => handleMarketplaceFormChange('title', e.target.value)}
              className="w-full bg-void border border-white/20 px-3 py-2 text-white focus:border-cyan-400 focus:outline-none" /></div>
          <div className="grid grid-cols-2 gap-4">
            <div><label className="block text-neutral-400 text-sm mb-1">Price *</label>
              <input type="number" step="0.01" value={marketplaceForm.price} onChange={(e) => handleMarketplaceFormChange('price', e.target.value)}
                className="w-full bg-void border border-white/20 px-3 py-2 text-white focus:border-cyan-400 focus:outline-none" /></div>
            <div><label className="block text-neutral-400 text-sm mb-1">Stock *</label>
              <input type="number" value={marketplaceForm.stock} onChange={(e) => handleMarketplaceFormChange('stock', e.target.value)}
                className="w-full bg-void border border-white/20 px-3 py-2 text-white focus:border-cyan-400 focus:outline-none" /></div>
          </div>
          <div><label className="block text-neutral-400 text-sm mb-1">Description</label>
            <textarea value={marketplaceForm.description} onChange={(e) => handleMarketplaceFormChange('description', e.target.value)} rows={3}
              className="w-full bg-void border border-white/20 px-3 py-2 text-white focus:border-cyan-400 focus:outline-none resize-none"></textarea></div>
          <div className="grid grid-cols-2 gap-4">
            <div><label className="block text-neutral-400 text-sm mb-1">Product Dimensions</label>
              <input type="text" value={marketplaceForm.dimensions} onChange={(e) => handleMarketplaceFormChange('dimensions', e.target.value)}
                placeholder="e.g., 6x4x5 inches"
                className="w-full bg-void border border-white/20 px-3 py-2 text-white focus:border-cyan-400 focus:outline-none" /></div>
            <div><label className="block text-neutral-400 text-sm mb-1">Materials</label>
              <input type="text" value={marketplaceForm.materials} onChange={(e) => handleMarketplaceFormChange('materials', e.target.value)}
                className="w-full bg-void border border-white/20 px-3 py-2 text-white focus:border-cyan-400 focus:outline-none" /></div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div><label className="block text-neutral-400 text-sm mb-1">Display Weight</label>
              <input type="text" value={marketplaceForm.weight} onChange={(e) => handleMarketplaceFormChange('weight', e.target.value)}
                placeholder="e.g., 2 lbs"
                className="w-full bg-void border border-white/20 px-3 py-2 text-white focus:border-cyan-400 focus:outline-none" /></div>
            <div><label className="block text-neutral-400 text-sm mb-1">Intended Use</label>
              <input type="text" value={marketplaceForm.intended_use} onChange={(e) => handleMarketplaceFormChange('intended_use', e.target.value)}
                className="w-full bg-void border border-white/20 px-3 py-2 text-white focus:border-cyan-400 focus:outline-none" /></div>
          </div>
          <div><label className="block text-neutral-400 text-sm mb-1">Shipping Info</label>
            <input type="text" value={marketplaceForm.shipping_info} onChange={(e) => handleMarketplaceFormChange('shipping_info', e.target.value)}
              className="w-full bg-void border border-white/20 px-3 py-2 text-white focus:border-cyan-400 focus:outline-none" /></div>
          
          {/* Shipping Box Dimensions for Shippo */}
          <div className="bg-cyan-400/5 border border-cyan-400/20 p-4 rounded">
            <div className="flex items-center justify-between mb-3">
              <h4 className="text-cyan-400 font-mono text-xs uppercase tracking-widest">Shipping Box Dimensions (for accurate quotes)</h4>
              <button
                type="button"
                onClick={() => {
                  handleMarketplaceFormChange('ship_weight', '0.5');
                  handleMarketplaceFormChange('ship_length', '10');
                  handleMarketplaceFormChange('ship_width', '8');
                  handleMarketplaceFormChange('ship_height', '1');
                }}
                className="px-3 py-1 bg-purple-500/20 border border-purple-500/40 text-purple-400 text-xs font-mono uppercase hover:bg-purple-500/30 transition-all"
              >
                Plastic Bag
              </button>
            </div>
            <div className="grid grid-cols-4 gap-3">
              <div><label className="block text-neutral-400 text-xs mb-1">Weight (lbs)</label>
                <input type="number" step="0.1" value={marketplaceForm.ship_weight} onChange={(e) => handleMarketplaceFormChange('ship_weight', e.target.value)}
                  className="w-full bg-void border border-white/20 px-3 py-2 text-white focus:border-cyan-400 focus:outline-none text-sm" /></div>
              <div><label className="block text-neutral-400 text-xs mb-1">Length (in)</label>
                <input type="number" step="0.5" value={marketplaceForm.ship_length} onChange={(e) => handleMarketplaceFormChange('ship_length', e.target.value)}
                  className="w-full bg-void border border-white/20 px-3 py-2 text-white focus:border-cyan-400 focus:outline-none text-sm" /></div>
              <div><label className="block text-neutral-400 text-xs mb-1">Width (in)</label>
                <input type="number" step="0.5" value={marketplaceForm.ship_width} onChange={(e) => handleMarketplaceFormChange('ship_width', e.target.value)}
                  className="w-full bg-void border border-white/20 px-3 py-2 text-white focus:border-cyan-400 focus:outline-none text-sm" /></div>
              <div><label className="block text-neutral-400 text-xs mb-1">Height (in)</label>
                <input type="number" step="0.5" value={marketplaceForm.ship_height} onChange={(e) => handleMarketplaceFormChange('ship_height', e.target.value)}
                  className="w-full bg-void border border-white/20 px-3 py-2 text-white focus:border-cyan-400 focus:outline-none text-sm" /></div>
            </div>
          </div>

          <div><label className="block text-neutral-400 text-sm mb-2">Images</label>
            {marketplaceForm.images.map((img, index) => (
              <div key={`marketplace-img-${index}`} className="flex gap-2 mb-2 items-center">
                <input type="text" placeholder="Image URL" value={img.url} onChange={(e) => handleMarketplaceImageChange(index, 'url', e.target.value)}
                  className="flex-1 bg-void border border-white/20 px-3 py-2 text-white focus:border-cyan-400 focus:outline-none text-sm" />
                <input type="text" placeholder="Alt text" value={img.alt} onChange={(e) => handleMarketplaceImageChange(index, 'alt', e.target.value)}
                  className="w-32 bg-void border border-white/20 px-3 py-2 text-white focus:border-cyan-400 focus:outline-none text-sm" />
                <label className="px-3 py-2 bg-cyan-400/20 border border-cyan-400/40 text-cyan-400 hover:bg-cyan-400/30 cursor-pointer transition-all flex items-center gap-1">
                  {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
                  <input type="file" accept="image/*" className="hidden" onChange={(e) => {
                    const file = e.target.files?.[0];
                    if (file) handleImageUpload(file, (url) => handleMarketplaceImageChange(index, 'url', url));
                    e.target.value = '';
                  }} />
                </label>
                {marketplaceForm.images.length > 1 && <button onClick={() => removeMarketplaceImageField(index)} className="px-2 text-red-400 hover:bg-red-500/20"><X className="w-4 h-4" /></button>}
              </div>))}
            <button type="button" onClick={addMarketplaceImageField} className="text-cyan-400 text-sm hover:text-cyan-300 flex items-center gap-1"><Plus className="w-4 h-4" /> Add another image</button></div>
        </div>
        <div className="p-4 border-t border-white/10 flex justify-end gap-3">
          <button onClick={() => setShowMarketplaceModal(false)} className="px-4 py-2 border border-white/20 text-neutral-400 hover:text-white transition-all">Cancel</button>
          <button onClick={saveMarketplaceProduct} className="px-4 py-2 bg-cyan-400 text-black font-mono uppercase tracking-widest text-sm hover:bg-cyan-300 transition-all flex items-center gap-2">
            <Save className="w-4 h-4" />{editingMarketplaceProduct ? 'Update' : 'Create'}</button>
        </div>
      </div>
    </div>
  );

  // Artist Modal - Calling Card Questionnaire
  const renderArtistModal = () => showArtistModal && (
    <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4 overflow-y-auto">
      <div className="bg-void-paper border border-white/10 w-full max-w-2xl my-8">
        <div className="flex items-center justify-between p-4 border-b border-white/10">
          <div>
            <h2 className="font-heading text-xl font-bold text-white">{editingArtist ? 'Edit Artist' : 'Add New Artist'}</h2>
            <p className="text-neutral-400 text-sm">Artist Calling Card Questionnaire</p>
          </div>
          <button onClick={() => setShowArtistModal(false)} className="text-neutral-400 hover:text-white"><X className="w-6 h-6" /></button>
        </div>
        <div className="p-4 space-y-4 max-h-[70vh] overflow-y-auto">
          {/* Basic Info Section */}
          <div className="bg-cyan-400/5 border border-cyan-400/20 p-4 rounded">
            <h3 className="text-cyan-400 font-mono text-sm uppercase tracking-widest mb-4">Basic Information</h3>
            <div className="grid grid-cols-2 gap-4">
              <div><label className="block text-neutral-400 text-sm mb-1">Artist Name *</label>
                <input type="text" value={artistForm.name} onChange={(e) => handleArtistFormChange('name', e.target.value)} placeholder="Display name"
                  className="w-full bg-void border border-white/20 px-3 py-2 text-white focus:border-cyan-400 focus:outline-none" /></div>
              <div><label className="block text-neutral-400 text-sm mb-1">Legal Name</label>
                <input type="text" value={artistForm.legal_name} onChange={(e) => handleArtistFormChange('legal_name', e.target.value)} placeholder="Full legal name"
                  className="w-full bg-void border border-white/20 px-3 py-2 text-white focus:border-cyan-400 focus:outline-none" /></div>
            </div>
            <div className="grid grid-cols-2 gap-4 mt-4">
              <div><label className="block text-neutral-400 text-sm mb-1">City (Public)</label>
                <input type="text" value={artistForm.city} onChange={(e) => handleArtistFormChange('city', e.target.value)} placeholder="City"
                  className="w-full bg-void border border-white/20 px-3 py-2 text-white focus:border-cyan-400 focus:outline-none" /></div>
              <div><label className="block text-neutral-400 text-sm mb-1">State (Public)</label>
                <input type="text" value={artistForm.state} onChange={(e) => handleArtistFormChange('state', e.target.value)} placeholder="State"
                  className="w-full bg-void border border-white/20 px-3 py-2 text-white focus:border-cyan-400 focus:outline-none" /></div>
            </div>
            <div className="mt-4"><label className="block text-neutral-400 text-sm mb-1">Website / Social Link</label>
              <input type="text" value={artistForm.website_url} onChange={(e) => handleArtistFormChange('website_url', e.target.value)} placeholder="https://..."
                className="w-full bg-void border border-white/20 px-3 py-2 text-white focus:border-cyan-400 focus:outline-none" /></div>
          </div>

          {/* Shipping Address Section (Private - for Shippo) */}
          <div className="bg-purple-500/5 border border-purple-500/20 p-4 rounded">
            <h3 className="text-purple-400 font-mono text-sm uppercase tracking-widest mb-2">Shipping Address (Private)</h3>
            <p className="text-neutral-500 text-xs mb-4">This address is used for shipping calculations only and is NOT displayed on the website. If left blank, items will ship from Only Skullz.</p>
            <div><label className="block text-neutral-400 text-sm mb-1">Street Address</label>
              <input type="text" value={artistForm.ship_street} onChange={(e) => handleArtistFormChange('ship_street', e.target.value)} placeholder="123 Main St"
                className="w-full bg-void border border-white/20 px-3 py-2 text-white focus:border-cyan-400 focus:outline-none" /></div>
            <div className="grid grid-cols-3 gap-4 mt-4">
              <div><label className="block text-neutral-400 text-sm mb-1">City</label>
                <input type="text" value={artistForm.ship_city} onChange={(e) => handleArtistFormChange('ship_city', e.target.value)} placeholder="City"
                  className="w-full bg-void border border-white/20 px-3 py-2 text-white focus:border-cyan-400 focus:outline-none" /></div>
              <div><label className="block text-neutral-400 text-sm mb-1">State</label>
                <input type="text" value={artistForm.ship_state} onChange={(e) => handleArtistFormChange('ship_state', e.target.value)} placeholder="CA"
                  className="w-full bg-void border border-white/20 px-3 py-2 text-white focus:border-cyan-400 focus:outline-none" /></div>
              <div><label className="block text-neutral-400 text-sm mb-1">ZIP Code</label>
                <input type="text" value={artistForm.ship_zip} onChange={(e) => handleArtistFormChange('ship_zip', e.target.value)} placeholder="12345"
                  className="w-full bg-void border border-white/20 px-3 py-2 text-white focus:border-cyan-400 focus:outline-none" /></div>
            </div>
          </div>

          {/* Calling Card Section */}
          <div className="bg-void border border-white/10 p-4 rounded">
            <h3 className="text-white font-mono text-sm uppercase tracking-widest mb-4">Calling Card Info</h3>
            
            <div><label className="block text-neutral-400 text-sm mb-1">Creative Title / Class *</label>
              <input type="text" value={artistForm.title} onChange={(e) => handleArtistFormChange('title', e.target.value)} placeholder="e.g., Maker of Skullz, Bonewood Crafter"
                className="w-full bg-void border border-white/20 px-3 py-2 text-white focus:border-cyan-400 focus:outline-none" /></div>
            
            <div className="mt-4"><label className="block text-neutral-400 text-sm mb-1">Description / Bio *</label>
              <textarea value={artistForm.description} onChange={(e) => handleArtistFormChange('description', e.target.value)} rows={3}
                placeholder="A brief description of the artist and their work..."
                className="w-full bg-void border border-white/20 px-3 py-2 text-white focus:border-cyan-400 focus:outline-none resize-none"></textarea></div>
            
            <div className="mt-4"><label className="block text-neutral-400 text-sm mb-1">Calling Card Image *</label>
              <div className="flex gap-2 items-center">
                <input type="text" value={artistForm.card_image} onChange={(e) => handleArtistFormChange('card_image', e.target.value)} placeholder="https://..."
                  className="flex-1 bg-void border border-white/20 px-3 py-2 text-white focus:border-cyan-400 focus:outline-none" />
                <label className="px-4 py-2 bg-cyan-400/20 border border-cyan-400/40 text-cyan-400 hover:bg-cyan-400/30 cursor-pointer transition-all flex items-center gap-2">
                  {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
                  <span className="text-sm">Upload</span>
                  <input type="file" accept="image/*" className="hidden" onChange={(e) => {
                    const file = e.target.files?.[0];
                    if (file) handleImageUpload(file, (url) => handleArtistFormChange('card_image', url));
                    e.target.value = '';
                  }} />
                </label>
              </div>
              {artistForm.card_image && (
                <img src={artistForm.card_image} alt="Card preview" className="mt-2 w-32 h-32 object-cover rounded border border-white/20" />
              )}</div>
          </div>

          {/* Powers Section */}
          <div className="bg-green-500/5 border border-green-500/20 p-4 rounded">
            <h3 className="text-green-400 font-mono text-sm uppercase tracking-widest mb-4">Powers (Strengths/Skills)</h3>
            {artistForm.powers.map((power, index) => (
              <div key={`power-${index}`} className="flex gap-2 mb-3">
                <input type="text" placeholder="Power name" value={power.name} onChange={(e) => handlePowerChange(index, 'name', e.target.value)}
                  className="w-1/3 bg-void border border-white/20 px-3 py-2 text-white focus:border-cyan-400 focus:outline-none text-sm" />
                <input type="text" placeholder="Description of this power..." value={power.description} onChange={(e) => handlePowerChange(index, 'description', e.target.value)}
                  className="flex-1 bg-void border border-white/20 px-3 py-2 text-white focus:border-cyan-400 focus:outline-none text-sm" />
                {artistForm.powers.length > 1 && (
                  <button onClick={() => removePowerField(index)} className="px-2 text-red-400 hover:bg-red-500/20"><X className="w-4 h-4" /></button>
                )}
              </div>))}
            <button type="button" onClick={addPowerField} className="text-green-400 text-sm hover:text-green-300 flex items-center gap-1">
              <Plus className="w-4 h-4" /> Add another power
            </button>
          </div>

          {/* Weakness Section */}
          <div className="bg-red-500/5 border border-red-500/20 p-4 rounded">
            <h3 className="text-red-400 font-mono text-sm uppercase tracking-widest mb-4">Weakness</h3>
            <input type="text" value={artistForm.weakness} onChange={(e) => handleArtistFormChange('weakness', e.target.value)}
              placeholder="e.g., Black Licorice Candy, Monster Energy Drinks"
              className="w-full bg-void border border-white/20 px-3 py-2 text-white focus:border-cyan-400 focus:outline-none" />
          </div>
        </div>
        <div className="p-4 border-t border-white/10 flex justify-end gap-3">
          <button onClick={() => setShowArtistModal(false)} className="px-4 py-2 border border-white/20 text-neutral-400 hover:text-white transition-all">Cancel</button>
          <button onClick={saveArtist} className="px-4 py-2 bg-cyan-400 text-black font-mono uppercase tracking-widest text-sm hover:bg-cyan-300 transition-all flex items-center gap-2">
            <Save className="w-4 h-4" />{editingArtist ? 'Update' : 'Create'}
          </button>
        </div>
      </div>
    </div>
  );

  // Main Dashboard
  return (
    <div className="pt-20 min-h-screen bg-void">
      {renderProductModal()}
      {renderMarketplaceModal()}
      {renderArtistModal()}
      
      <div className="bg-void-paper border-b border-white/10 px-6 py-4">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <h1 className="font-heading text-xl font-bold text-white">Admin Dashboard</h1>
          <button onClick={handleLogout} className="text-neutral-400 hover:text-white text-sm font-mono uppercase tracking-widest">Logout</button>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 py-8">
        <div className="flex flex-wrap gap-2 mb-8 border-b border-white/10 pb-4">
          {[
            { id: 'dashboard', label: 'Dashboard', icon: BarChart3 },
            { id: 'orders', label: 'Orders', icon: ShoppingCart },
            { id: 'products', label: 'Products', icon: Package },
            { id: 'categories', label: 'Categories', icon: FolderOpen },
            { id: 'marketplace', label: 'Marketplace', icon: Store },
            { id: 'artists', label: 'Artists', icon: Users },
            { id: 'subscribers', label: 'Subscribers', icon: Heart, badge: newSubscriberCount },
            { id: 'discounts', label: 'Discounts', icon: Tag },
            { id: 'bto', label: 'BTO Orders', icon: Hammer },
            { id: 'gallery', label: 'Gallery', icon: Camera },
            { id: 'links', label: 'Quick Links', icon: Link2 },
            { id: 'messages', label: 'Messages', icon: Mail },
          ].map((tab) => (
            <button key={tab.id} onClick={() => setActiveTab(tab.id)} data-testid={`admin-tab-${tab.id}`}
              className={`flex items-center gap-2 px-4 py-2 font-mono text-sm uppercase tracking-widest transition-all relative ${
                activeTab === tab.id ? 'bg-cyan-400 text-black' : 'bg-void-paper border border-white/10 text-neutral-400 hover:text-white'}`}>
              <tab.icon className="w-4 h-4" />{tab.label}
              {tab.badge > 0 && (
                <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs w-5 h-5 rounded-full flex items-center justify-center">
                  {tab.badge}
                </span>
              )}
            </button>
          ))}
        </div>

        {loading ? (
          <div className="text-center py-12"><div className="animate-pulse text-cyan-400 font-mono">Loading...</div></div>
        ) : (
          <>
            {/* Dashboard Tab */}
            {activeTab === 'dashboard' && stats && (
              <div className="space-y-8">
                {/* Main Stats */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div className="bg-void-paper border border-white/10 p-6">
                    <p className="text-neutral-400 text-sm font-mono uppercase mb-2">Total Orders</p>
                    <p className="text-3xl font-bold text-white">{stats.total_orders}</p></div>
                  <div className="bg-void-paper border border-white/10 p-6">
                    <p className="text-neutral-400 text-sm font-mono uppercase mb-2">Total Revenue</p>
                    <p className="text-3xl font-bold text-cyan-400">${(stats.total_sales || 0).toFixed(2)}</p></div>
                  <div className="bg-void-paper border border-white/10 p-6">
                    <p className="text-neutral-400 text-sm font-mono uppercase mb-2">Products</p>
                    <p className="text-3xl font-bold text-white">{stats.total_products}</p></div>
                  <div className="bg-void-paper border border-white/10 p-6">
                    <p className="text-neutral-400 text-sm font-mono uppercase mb-2">Visitors</p>
                    <p className="text-3xl font-bold text-white">{stats.visitor_count}</p></div>
                </div>

                {/* Sales Breakdown */}
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                  <div className="bg-cyan-400/10 border border-cyan-400/30 p-6">
                    <p className="text-cyan-400 text-sm font-mono uppercase mb-2">Shop Sales</p>
                    <p className="text-2xl font-bold text-white">${(stats.shop_sales || 0).toFixed(2)}</p>
                    <p className="text-neutral-500 text-xs mt-1">From your products</p></div>
                  <div className="bg-purple-400/10 border border-purple-400/30 p-6">
                    <p className="text-purple-400 text-sm font-mono uppercase mb-2">Marketplace Sales</p>
                    <p className="text-2xl font-bold text-white">${(stats.marketplace_sales || 0).toFixed(2)}</p>
                    <p className="text-neutral-500 text-xs mt-1">From artist products</p></div>
                  <div className="bg-green-400/10 border border-green-400/30 p-6">
                    <p className="text-green-400 text-sm font-mono uppercase mb-2">Shipping Revenue</p>
                    <p className="text-2xl font-bold text-white">${(stats.shipping_revenue || 0).toFixed(2)}</p>
                    <p className="text-neutral-500 text-xs mt-1">Total shipping collected</p></div>
                  <div className="bg-amber-400/10 border border-amber-400/30 p-6">
                    <p className="text-amber-400 text-sm font-mono uppercase mb-2">Tax Collected (IL)</p>
                    <p className="text-2xl font-bold text-white">${(stats.tax_collected || 0).toFixed(2)}</p>
                    <p className="text-neutral-500 text-xs mt-1">Illinois sales tax</p></div>
                </div>

                {/* Settings Section */}
                <div className="bg-void-paper border border-white/10 p-6">
                  <h3 className="font-heading text-lg font-bold text-white mb-4">Site Settings</h3>
                  <div className="flex items-center justify-between py-3 border-b border-white/5">
                    <div className="flex items-center gap-3">
                      {notificationEnabled ? <Bell className="w-5 h-5 text-cyan-400" /> : <BellOff className="w-5 h-5 text-neutral-500" />}
                      <div>
                        <p className="text-white font-medium">Live Purchase Notifications</p>
                        <p className="text-neutral-500 text-xs">Show "Someone just purchased..." popups to visitors</p>
                      </div>
                    </div>
                    <button
                      onClick={toggleNotificationSetting}
                      className={`relative w-14 h-7 rounded-full transition-colors ${notificationEnabled ? 'bg-cyan-400' : 'bg-neutral-700'}`}
                    >
                      <span className={`absolute top-1 w-5 h-5 bg-white rounded-full transition-transform ${notificationEnabled ? 'left-8' : 'left-1'}`} />
                    </button>
                  </div>
                </div>

                {stats.low_stock_products?.length > 0 && (
                  <div className="bg-red-500/10 border border-red-500/30 p-6">
                    <div className="flex items-center gap-3 mb-4"><AlertTriangle className="w-5 h-5 text-red-400" /><h3 className="font-heading text-lg font-bold text-white">Low Stock Alert</h3></div>
                    <div className="space-y-2">{stats.low_stock_products.map((product) => (
                      <div key={product.id} className="flex items-center justify-between text-sm">
                        <span className="text-white">{product.title}</span><span className="text-red-400 font-mono">{product.stock} left</span></div>))}</div>
                  </div>)}
                <div className="bg-void-paper border border-white/10 p-6">
                  <h3 className="font-heading text-lg font-bold text-white mb-4">Recent Orders</h3>
                  <div className="space-y-3">{stats.recent_orders?.map((order) => (
                    <div key={order.id} className="flex items-center justify-between py-2 border-b border-white/5">
                      <div><p className="text-white text-sm">{order.customer_info?.first_name} {order.customer_info?.last_name}</p>
                        <p className="text-neutral-500 text-xs font-mono">{order.id.slice(0, 8)}</p></div>
                      <div className="text-right"><p className="text-cyan-400 font-mono">${order.total?.toFixed(2)}</p>
                        <p className={`text-xs font-mono uppercase ${order.status === 'completed' ? 'text-green-400' : order.status === 'shipped' ? 'text-blue-400' : 'text-yellow-400'}`}>{order.status}</p></div>
                    </div>))}</div>
                </div>
              </div>)}

            {/* Orders Tab */}
            {activeTab === 'orders' && (
              <div className="space-y-4">
                <div className="flex justify-end mb-4">
                  <button onClick={exportOrders} className="flex items-center gap-2 bg-void-paper border border-white/10 px-4 py-2 text-sm font-mono uppercase tracking-widest text-neutral-400 hover:text-white transition-all">
                    <Download className="w-4 h-4" />Export CSV</button></div>
                {orders.length === 0 ? <p className="text-neutral-400 text-center py-8">No orders yet</p> : orders.map((order) => (
                  <div key={order.id} className="bg-void-paper border border-white/10">
                    <div className="p-4 flex items-center justify-between cursor-pointer" onClick={() => setExpandedOrder(expandedOrder === order.id ? null : order.id)}>
                      <div className="flex items-center gap-4">
                        {expandedOrder === order.id ? <ChevronUp className="w-4 h-4 text-neutral-400" /> : <ChevronDown className="w-4 h-4 text-neutral-400" />}
                        <div><p className="text-white font-semibold">{order.customer_info?.first_name} {order.customer_info?.last_name}</p>
                          <p className="text-neutral-500 text-xs font-mono">{order.id}</p></div></div>
                      <div className="flex items-center gap-4">
                        <p className="text-cyan-400 font-mono font-bold">${order.total?.toFixed(2)}</p>
                        <select value={order.status || 'pending'} onChange={(e) => { e.stopPropagation(); updateOrderStatus(order.id, e.target.value); }} onClick={(e) => e.stopPropagation()}
                          className="bg-void border border-white/20 text-white text-sm px-3 py-1 focus:outline-none focus:border-cyan-400">
                          <option value="pending">Pending</option><option value="processing">Processing</option>
                          <option value="shipped">Shipped</option><option value="completed">Completed</option><option value="cancelled">Cancelled</option>
                        </select></div></div>
                    {expandedOrder === order.id && (
                      <div className="border-t border-white/10 p-4 bg-void/50">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <div><h4 className="text-neutral-400 text-xs font-mono uppercase mb-2">Customer Info</h4>
                            <p className="text-white text-sm">{order.customer_info?.email}</p>
                            <p className="text-neutral-400 text-sm">{order.customer_info?.address}</p>
                            <p className="text-neutral-400 text-sm">{order.customer_info?.city}, {order.customer_info?.state} {order.customer_info?.zip_code}</p></div>
                          <div><h4 className="text-neutral-400 text-xs font-mono uppercase mb-2">Items</h4>
                            {order.items?.map((item, idx) => (
                              <div key={idx} className="text-white text-sm">
                                <span>{item.product_name || item.product_id} x {item.quantity}</span>
                                {item.is_bto && <span className="ml-2 text-amber-400 text-xs">[BTO]</span>}
                                {item.color_preference && <p className="text-neutral-400 text-xs ml-2">Color: {item.color_preference}</p>}
                              </div>
                            ))}</div></div>
                        {/* Shipping & Tracking Info */}
                        {order.tracking_number && (
                          <div className="mt-4 p-3 bg-green-500/10 border border-green-500/30 rounded">
                            <h4 className="text-green-400 text-xs font-mono uppercase mb-2">📦 Shipping Label Created</h4>
                            <p className="text-white text-sm">Tracking: <span className="font-mono">{order.tracking_number}</span></p>
                            {order.tracking_url && <a href={order.tracking_url} target="_blank" rel="noopener noreferrer" className="text-cyan-400 text-sm hover:underline">Track Package →</a>}
                            {order.label_url && <a href={order.label_url} target="_blank" rel="noopener noreferrer" className="text-amber-400 text-sm ml-4 hover:underline">Print Label →</a>}
                          </div>
                        )}
                        <p className="text-neutral-500 text-xs mt-4">PayPal Order: {order.paypal_order_id}</p></div>)}
                  </div>))}
              </div>)}

            {/* Products Tab */}
            {activeTab === 'products' && (
              <div className="space-y-4">
                <div className="flex justify-between items-center mb-4">
                  <h2 className="text-white font-heading text-lg">Shop Products</h2>
                  <button onClick={openAddProduct} data-testid="add-product-btn" className="flex items-center gap-2 bg-cyan-400 text-black px-4 py-2 text-sm font-mono uppercase tracking-widest hover:bg-cyan-300 transition-all">
                    <Plus className="w-4 h-4" />Add Product</button></div>
                {products.length === 0 ? <p className="text-neutral-400 text-center py-8">No products</p> : (
                  <div className="grid gap-4">{products.map((product) => (
                    <div key={product.id} className="bg-void-paper border border-white/10 p-4 flex items-center gap-4">
                      <img src={product.images?.[0]?.url} alt={product.title} className="w-16 h-16 object-cover" />
                      <div className="flex-1 min-w-0"><h3 className="text-white font-semibold truncate">{product.title}</h3>
                        <p className="text-cyan-400 font-mono">${product.price?.toFixed(2)}</p></div>
                      <div className="flex items-center gap-3">
                        <div className="flex items-center gap-2"><label className="text-neutral-400 text-xs">Stock:</label>
                          <input type="number" value={product.stock} onChange={(e) => quickUpdateStock(product.id, e.target.value)}
                            className={`w-16 bg-void border px-2 py-1 text-white text-center focus:outline-none focus:border-cyan-400 ${product.stock <= 2 ? 'border-red-500/50' : 'border-white/20'}`} /></div>
                        <button onClick={() => openEditProduct(product)} className="p-2 text-cyan-400 hover:bg-cyan-400/20 transition-all" title="Edit"><Edit className="w-4 h-4" /></button>
                        <button onClick={() => deleteProduct(product.id)} className="p-2 text-red-400 hover:bg-red-500/20 transition-all" title="Delete"><Trash2 className="w-4 h-4" /></button>
                      </div></div>))}</div>)}
              </div>)}

            {/* Categories Tab */}
            {activeTab === 'categories' && (
              <div className="space-y-4">
                <div className="flex justify-between items-center mb-4">
                  <h2 className="text-white font-heading text-lg">Product Categories</h2>
                  <button onClick={() => setShowCategoryModal(true)} data-testid="add-category-btn" className="flex items-center gap-2 bg-cyan-400 text-black px-4 py-2 text-sm font-mono uppercase tracking-widest hover:bg-cyan-300 transition-all">
                    <Plus className="w-4 h-4" />Add Category</button>
                </div>
                
                {categories.length === 0 ? (
                  <p className="text-neutral-400 text-center py-8">No categories yet</p>
                ) : (
                  <div className="grid gap-3">
                    {categories.map((cat) => (
                      <div key={cat.id} className="bg-void-paper border border-white/10 p-4 flex items-center justify-between">
                        <div>
                          <h3 className="text-white font-semibold">{cat.name}</h3>
                          <p className="text-neutral-400 text-sm font-mono">/{cat.slug}</p>
                          {cat.description && <p className="text-neutral-500 text-xs mt-1">{cat.description}</p>}
                        </div>
                        <button 
                          onClick={async () => {
                            if (window.confirm(`Delete category "${cat.name}"? Products in this category will become uncategorized.`)) {
                              try {
                                await api.deleteCategory(cat.id, storedPassword);
                                setCategories(categories.filter(c => c.id !== cat.id));
                              } catch (error) {
                                alert('Failed to delete category');
                              }
                            }
                          }} 
                          className="p-2 text-red-400 hover:bg-red-500/20 transition-all" 
                          title="Delete"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}

                {/* Add Category Modal */}
                {showCategoryModal && (
                  <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
                    <div className="bg-void-paper border border-white/10 w-full max-w-md">
                      <div className="p-4 border-b border-white/10 flex items-center justify-between">
                        <h3 className="text-white font-heading text-lg">Add Category</h3>
                        <button onClick={() => { setShowCategoryModal(false); setCategoryForm({ name: '', slug: '', description: '' }); }} className="text-neutral-400 hover:text-white">
                          <X className="w-5 h-5" />
                        </button>
                      </div>
                      <form onSubmit={async (e) => {
                        e.preventDefault();
                        if (!categoryForm.name || !categoryForm.slug) {
                          alert('Name and slug are required');
                          return;
                        }
                        try {
                          const newCat = await api.createCategory(categoryForm, storedPassword);
                          setCategories([...categories, newCat]);
                          setShowCategoryModal(false);
                          setCategoryForm({ name: '', slug: '', description: '' });
                        } catch (error) {
                          alert(error.response?.data?.detail || 'Failed to create category');
                        }
                      }} className="p-4 space-y-4">
                        <div>
                          <label className="block text-sm text-neutral-400 mb-2">Name *</label>
                          <input
                            type="text"
                            value={categoryForm.name}
                            onChange={(e) => {
                              const name = e.target.value;
                              const slug = name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');
                              setCategoryForm({ ...categoryForm, name, slug });
                            }}
                            placeholder="e.g., Coming Soon"
                            className="w-full bg-void border border-white/10 p-3 text-white"
                          />
                        </div>
                        <div>
                          <label className="block text-sm text-neutral-400 mb-2">Slug *</label>
                          <input
                            type="text"
                            value={categoryForm.slug}
                            onChange={(e) => setCategoryForm({ ...categoryForm, slug: e.target.value.toLowerCase().replace(/[^a-z0-9-]+/g, '') })}
                            placeholder="e.g., coming-soon"
                            className="w-full bg-void border border-white/10 p-3 text-white font-mono"
                          />
                          <p className="text-xs text-neutral-500 mt-1">URL-friendly identifier (auto-generated from name)</p>
                        </div>
                        <div>
                          <label className="block text-sm text-neutral-400 mb-2">Description (optional)</label>
                          <textarea
                            value={categoryForm.description}
                            onChange={(e) => setCategoryForm({ ...categoryForm, description: e.target.value })}
                            placeholder="Brief description of this category"
                            rows={2}
                            className="w-full bg-void border border-white/10 p-3 text-white resize-none"
                          />
                        </div>
                        <button type="submit" className="w-full bg-cyan-400 text-black font-mono uppercase tracking-widest py-3 hover:bg-cyan-300 transition-all flex items-center justify-center gap-2">
                          <Save className="w-4 h-4" /> Create Category
                        </button>
                      </form>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Marketplace Tab */}
            {activeTab === 'marketplace' && (
              <div className="space-y-4">
                <div className="flex justify-between items-center mb-4">
                  <h2 className="text-white font-heading text-lg">Marketplace Products (Artist Work)</h2>
                  <button onClick={openAddMarketplaceProduct} data-testid="add-marketplace-product-btn" className="flex items-center gap-2 bg-cyan-400 text-black px-4 py-2 text-sm font-mono uppercase tracking-widest hover:bg-cyan-300 transition-all">
                    <Plus className="w-4 h-4" />Add Artist Product</button></div>
                {marketplaceProducts.length === 0 ? <p className="text-neutral-400 text-center py-8">No marketplace products yet</p> : (
                  <div className="grid gap-4">{marketplaceProducts.map((product) => (
                    <div key={product.id} className="bg-void-paper border border-white/10 p-4 flex items-center gap-4">
                      <img src={product.images?.[0]?.url} alt={product.title} className="w-16 h-16 object-cover" />
                      <div className="flex-1 min-w-0"><h3 className="text-white font-semibold truncate">{product.title}</h3>
                        <p className="text-neutral-400 text-sm">by {getArtistName(product.artist_id)}</p>
                        <p className="text-cyan-400 font-mono">{product.price > 0 ? `$${product.price?.toFixed(2)}` : 'FREE'}</p></div>
                      <div className="flex items-center gap-3">
                        <div className="flex items-center gap-2"><label className="text-neutral-400 text-xs">Stock:</label>
                          <input type="number" value={product.stock} onChange={(e) => quickUpdateMarketplaceStock(product.id, e.target.value)}
                            className={`w-16 bg-void border px-2 py-1 text-white text-center focus:outline-none focus:border-cyan-400 ${product.stock <= 0 ? 'border-red-500/50' : 'border-white/20'}`} /></div>
                        <button onClick={() => openEditMarketplaceProduct(product)} className="p-2 text-cyan-400 hover:bg-cyan-400/20 transition-all" title="Edit"><Edit className="w-4 h-4" /></button>
                        <button onClick={() => deleteMarketplaceProduct(product.id)} className="p-2 text-red-400 hover:bg-red-500/20 transition-all" title="Delete"><Trash2 className="w-4 h-4" /></button>
                      </div></div>))}</div>)}
              </div>)}

            {/* Artists Tab */}
            {activeTab === 'artists' && (
              <div className="space-y-4">
                <div className="flex justify-between items-center mb-4">
                  <h2 className="text-white font-heading text-lg">Marketplace Artists</h2>
                  <button onClick={openAddArtist} data-testid="add-artist-btn" className="flex items-center gap-2 bg-cyan-400 text-black px-4 py-2 text-sm font-mono uppercase tracking-widest hover:bg-cyan-300 transition-all">
                    <Plus className="w-4 h-4" />Add Artist</button></div>
                
                {/* Questionnaire Reference Link */}
                <div className="bg-cyan-400/5 border border-cyan-400/20 p-4 rounded flex items-center justify-between">
                  <div>
                    <p className="text-white font-semibold">Artist Calling Card Questionnaire</p>
                    <p className="text-neutral-400 text-sm">Reference document for new artist applications</p>
                  </div>
                  <a href="https://customer-assets.emergentagent.com/job_e82238a4-d209-442e-aeb8-040c776106cd/artifacts/3npholt3_angel%20calling%20card%20questionnaire.pdf"
                    target="_blank" rel="noopener noreferrer"
                    className="flex items-center gap-2 text-cyan-400 hover:text-cyan-300 text-sm font-mono">
                    <ExternalLink className="w-4 h-4" />View PDF
                  </a>
                </div>

                {artists.length === 0 ? <p className="text-neutral-400 text-center py-8">No artists yet</p> : (
                  <div className="grid gap-4">{artists.map((artist) => (
                    <div key={artist.id} className="bg-void-paper border border-white/10">
                      <div className="p-4 flex items-center gap-4 cursor-pointer" onClick={() => setExpandedArtist(expandedArtist === artist.id ? null : artist.id)}>
                        {expandedArtist === artist.id ? <ChevronUp className="w-4 h-4 text-neutral-400" /> : <ChevronDown className="w-4 h-4 text-neutral-400" />}
                        <img src={artist.card_image} alt={artist.name} className="w-16 h-16 object-cover rounded" />
                        <div className="flex-1">
                          <h3 className="text-white font-semibold">{artist.name}</h3>
                          <p className="text-cyan-400 text-sm">{artist.title}</p>
                          {(artist.city || artist.state) && (
                            <p className="text-neutral-500 text-xs flex items-center gap-1"><MapPin className="w-3 h-3" />{artist.city}{artist.city && artist.state && ', '}{artist.state}</p>
                          )}
                        </div>
                        <div className="flex items-center gap-2">
                          <button onClick={(e) => { e.stopPropagation(); openEditArtist(artist); }} className="p-2 text-cyan-400 hover:bg-cyan-400/20 transition-all" title="Edit"><Edit className="w-4 h-4" /></button>
                          <button onClick={(e) => { e.stopPropagation(); deleteArtist(artist.id); }} className="p-2 text-red-400 hover:bg-red-500/20 transition-all" title="Delete"><Trash2 className="w-4 h-4" /></button>
                        </div>
                      </div>
                      {expandedArtist === artist.id && (
                        <div className="border-t border-white/10 p-4 bg-void/50 space-y-4">
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div>
                              <h4 className="text-neutral-400 text-xs font-mono uppercase mb-2">Legal Name</h4>
                              <p className="text-white text-sm">{artist.legal_name || 'Not provided'}</p>
                            </div>
                            <div>
                              <h4 className="text-neutral-400 text-xs font-mono uppercase mb-2">Website</h4>
                              {artist.website_url ? (
                                <a href={artist.website_url} target="_blank" rel="noopener noreferrer" className="text-cyan-400 text-sm hover:underline flex items-center gap-1">
                                  <ExternalLink className="w-3 h-3" />{artist.website_url}
                                </a>
                              ) : <p className="text-neutral-500 text-sm">Not provided</p>}
                            </div>
                          </div>
                          <div>
                            <h4 className="text-neutral-400 text-xs font-mono uppercase mb-2">Bio</h4>
                            <p className="text-neutral-300 text-sm">{artist.description}</p>
                          </div>
                          <div>
                            <h4 className="text-green-400 text-xs font-mono uppercase mb-2">Powers</h4>
                            <ul className="space-y-1">
                              {artist.powers?.map((power, idx) => (
                                <li key={idx} className="text-sm"><span className="text-white font-semibold">{power.name}</span> - <span className="text-neutral-400">{power.description}</span></li>
                              ))}
                            </ul>
                          </div>
                          <div>
                            <h4 className="text-red-400 text-xs font-mono uppercase mb-2">Weakness</h4>
                            <p className="text-neutral-300 text-sm">{artist.weakness}</p>
                          </div>
                        </div>
                      )}
                    </div>))}</div>)}
              </div>)}

            {/* Messages Tab */}
            {activeTab === 'messages' && (
              <div className="space-y-4">
                {contacts.length === 0 ? <p className="text-neutral-400 text-center py-8">No messages</p> : contacts.map((contact) => (
                  <div key={contact.id} className="bg-void-paper border border-white/10 p-4">
                    <div className="flex items-start justify-between mb-2">
                      <div><h3 className="text-white font-semibold">{contact.name}</h3><p className="text-cyan-400 text-sm">{contact.email}</p></div>
                      <button onClick={() => deleteContact(contact.id)} className="p-2 text-red-400 hover:bg-red-500/20 transition-all"><Trash2 className="w-4 h-4" /></button></div>
                    <p className="text-neutral-400 text-xs font-mono uppercase mb-2">{contact.subject}</p>
                    <p className="text-neutral-300 text-sm">{contact.message}</p>
                    <p className="text-neutral-500 text-xs mt-2">{new Date(contact.created_at).toLocaleString()}</p>
                  </div>))}
              </div>)}

            {/* Subscribers Tab */}
            {activeTab === 'subscribers' && (
              <div className="space-y-4">
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-xl text-white font-semibold">Newsletter Subscribers</h2>
                  <span className="text-cyan-400 text-sm">{subscribers.length} total subscribers</span>
                </div>
                {subscribers.length === 0 ? (
                  <p className="text-neutral-400 text-center py-8">No subscribers yet</p>
                ) : (
                  <div className="bg-void-paper border border-white/10 overflow-hidden">
                    <table className="w-full">
                      <thead className="bg-white/5">
                        <tr>
                          <th className="text-left text-neutral-400 text-xs font-mono uppercase p-4">Email</th>
                          <th className="text-left text-neutral-400 text-xs font-mono uppercase p-4">Subscribed On</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-white/10">
                        {subscribers.map((subscriber, index) => (
                          <tr key={subscriber.email || index} className="hover:bg-white/5 transition-colors">
                            <td className="p-4">
                              <span className="text-cyan-400">{subscriber.email}</span>
                            </td>
                            <td className="p-4">
                              <span className="text-neutral-300 text-sm">
                                {new Date(subscriber.subscribed_at).toLocaleString()}
                              </span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>)}

            {/* Gallery Tab */}
            {activeTab === 'gallery' && (
              <div className="space-y-6">
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-xl text-white font-semibold flex items-center gap-2">
                    <Camera className="w-5 h-5 text-cyan-400" />
                    Company Gallery
                  </h2>
                  <button
                    onClick={() => {
                      setEditingGalleryPhoto(null);
                      setGalleryForm({ url: '', caption: '' });
                      setShowGalleryModal(true);
                    }}
                    className="flex items-center gap-2 px-4 py-2 bg-cyan-400 text-black font-mono text-sm uppercase tracking-widest hover:bg-cyan-300 transition-all"
                  >
                    <Plus className="w-4 h-4" /> Add Photo
                  </button>
                </div>
                <p className="text-neutral-400 text-sm">
                  Photos added here will appear on the Reviews page in a fading slideshow. Click the edit icon to update captions.
                </p>

                {galleryPhotos.length === 0 ? (
                  <p className="text-neutral-400 text-center py-8">No photos added yet</p>
                ) : (
                  <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                    {galleryPhotos.map((photo) => (
                      <div key={photo.id} className="bg-void-paper border border-white/10 overflow-hidden group relative">
                        <img
                          src={photo.url}
                          alt={photo.caption || 'Gallery photo'}
                          className="w-full aspect-video object-cover"
                        />
                        {/* Inline Caption Edit */}
                        {inlineEditingPhotoId === photo.id ? (
                          <div className="p-2 flex items-center gap-2">
                            <input
                              type="text"
                              value={inlineEditCaption}
                              onChange={(e) => setInlineEditCaption(e.target.value)}
                              placeholder="Enter caption..."
                              className="flex-1 bg-void border border-cyan-400/50 px-2 py-1 text-white text-xs focus:outline-none"
                              autoFocus
                              onKeyDown={async (e) => {
                                if (e.key === 'Enter') {
                                  try {
                                    const updated = await api.updateGalleryPhoto(photo.id, { caption: inlineEditCaption }, storedPassword);
                                    setGalleryPhotos(galleryPhotos.map(p => p.id === photo.id ? updated : p));
                                    setInlineEditingPhotoId(null);
                                  } catch (error) {
                                    console.error('Error updating caption:', error);
                                    alert('Failed to update caption');
                                  }
                                } else if (e.key === 'Escape') {
                                  setInlineEditingPhotoId(null);
                                }
                              }}
                            />
                            <button
                              onClick={async () => {
                                try {
                                  const updated = await api.updateGalleryPhoto(photo.id, { caption: inlineEditCaption }, storedPassword);
                                  setGalleryPhotos(galleryPhotos.map(p => p.id === photo.id ? updated : p));
                                  setInlineEditingPhotoId(null);
                                } catch (error) {
                                  console.error('Error updating caption:', error);
                                  alert('Failed to update caption');
                                }
                              }}
                              className="p-1 text-green-400 hover:bg-green-400/20"
                            >
                              <Save className="w-3 h-3" />
                            </button>
                            <button
                              onClick={() => setInlineEditingPhotoId(null)}
                              className="p-1 text-red-400 hover:bg-red-400/20"
                            >
                              <X className="w-3 h-3" />
                            </button>
                          </div>
                        ) : (
                          <div 
                            className="p-2 text-xs text-neutral-400 truncate cursor-pointer hover:text-white transition-colors"
                            onClick={() => {
                              setInlineEditingPhotoId(photo.id);
                              setInlineEditCaption(photo.caption || '');
                            }}
                            title="Click to edit caption"
                          >
                            {photo.caption || <span className="text-neutral-600 italic">No caption - click to add</span>}
                          </div>
                        )}
                        {/* Action buttons overlay */}
                        <div className="absolute top-2 right-2 flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                          <button
                            onClick={() => {
                              setEditingGalleryPhoto(photo);
                              setGalleryForm({ url: photo.url, caption: photo.caption || '' });
                              setShowGalleryModal(true);
                            }}
                            className="p-2 bg-cyan-400/80 text-black rounded hover:bg-cyan-300"
                            title="Edit in modal"
                          >
                            <Edit className="w-4 h-4" />
                          </button>
                          <button
                            onClick={async () => {
                              if (window.confirm('Delete this photo?')) {
                                try {
                                  await api.deleteGalleryPhoto(photo.id, storedPassword);
                                  setGalleryPhotos(galleryPhotos.filter(p => p.id !== photo.id));
                                } catch (error) {
                                  console.error('Error deleting photo:', error);
                                  alert('Failed to delete photo');
                                }
                              }
                            }}
                            className="p-2 bg-red-500/80 text-white rounded hover:bg-red-400"
                            title="Delete photo"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>)}

            {/* Discounts Tab */}
            {activeTab === 'discounts' && (
              <div className="space-y-6">
                {/* Discount Stats Cards */}
                {discountStats && (
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <div className="bg-green-500/10 border border-green-500/30 p-4">
                      <p className="text-green-400 text-xs font-mono uppercase mb-1">Total Discounts Given</p>
                      <p className="text-2xl font-bold text-white">${discountStats.total_discount_given?.toFixed(2) || '0.00'}</p>
                    </div>
                    <div className="bg-cyan-500/10 border border-cyan-500/30 p-4">
                      <p className="text-cyan-400 text-xs font-mono uppercase mb-1">Total Uses</p>
                      <p className="text-2xl font-bold text-white">{discountStats.total_usage || 0}</p>
                    </div>
                    <div className="bg-purple-500/10 border border-purple-500/30 p-4">
                      <p className="text-purple-400 text-xs font-mono uppercase mb-1">Active Codes</p>
                      <p className="text-2xl font-bold text-white">{discountStats.active_codes || 0}</p>
                    </div>
                    <div className="bg-neutral-500/10 border border-neutral-500/30 p-4">
                      <p className="text-neutral-400 text-xs font-mono uppercase mb-1">Total Codes</p>
                      <p className="text-2xl font-bold text-white">{discountStats.total_codes || 0}</p>
                    </div>
                  </div>
                )}

                <div className="flex items-center justify-between">
                  <h2 className="text-xl text-white font-semibold flex items-center gap-2">
                    <Tag className="w-5 h-5 text-cyan-400" />
                    Discount Codes
                  </h2>
                  <button
                    onClick={() => {
                      setEditingDiscount(null);
                      setDiscountForm({ code: '', discount_type: 'percentage', discount_value: '', usage_limit: '', expires_at: '', is_active: true });
                      setShowDiscountModal(true);
                    }}
                    className="flex items-center gap-2 px-4 py-2 bg-cyan-400 text-black font-mono text-sm uppercase tracking-widest hover:bg-cyan-300 transition-all"
                    data-testid="add-discount-btn"
                  >
                    <Plus className="w-4 h-4" /> Create Code
                  </button>
                </div>
                <p className="text-neutral-400 text-sm">
                  Create discount codes for customers. Each code can only be used once per customer.
                </p>

                {discountCodes.length === 0 ? (
                  <div className="bg-void-paper border border-white/10 p-8 text-center">
                    <Tag className="w-12 h-12 text-neutral-600 mx-auto mb-4" />
                    <p className="text-neutral-400">No discount codes created yet</p>
                    <p className="text-neutral-500 text-sm mt-1">Create your first discount code to offer special deals to customers</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {discountCodes.map((discount) => (
                      <div key={discount.id} className={`bg-void-paper border p-4 ${discount.is_active ? 'border-white/10' : 'border-red-500/30 opacity-60'}`}>
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-4">
                            <div className={`px-3 py-1 font-mono text-lg font-bold ${discount.is_active ? 'bg-cyan-400/20 text-cyan-400' : 'bg-neutral-500/20 text-neutral-400'}`}>
                              {discount.code}
                            </div>
                            <div className="flex items-center gap-2">
                              {discount.discount_type === 'percentage' ? (
                                <Percent className="w-4 h-4 text-green-400" />
                              ) : (
                                <DollarSign className="w-4 h-4 text-green-400" />
                              )}
                              <span className="text-white font-semibold">
                                {discount.discount_type === 'percentage' ? `${discount.discount_value}% off` : `$${discount.discount_value} off`}
                              </span>
                            </div>
                            {/* Expiration badge */}
                            {discount.expires_at && (
                              <span className={`px-2 py-0.5 text-xs font-mono flex items-center gap-1 ${
                                new Date(discount.expires_at) < new Date() 
                                  ? 'bg-red-500/20 text-red-400' 
                                  : 'bg-yellow-500/20 text-yellow-400'
                              }`}>
                                <Calendar className="w-3 h-3" />
                                {new Date(discount.expires_at) < new Date() ? 'Expired' : `Expires ${new Date(discount.expires_at).toLocaleDateString()}`}
                              </span>
                            )}
                            {!discount.is_active && (
                              <span className="px-2 py-0.5 bg-red-500/20 text-red-400 text-xs font-mono uppercase">Inactive</span>
                            )}
                          </div>
                          <div className="flex items-center gap-2">
                            <button
                              onClick={() => {
                                setEditingDiscount(discount);
                                setDiscountForm({
                                  code: discount.code,
                                  discount_type: discount.discount_type,
                                  discount_value: discount.discount_value,
                                  usage_limit: discount.usage_limit || '',
                                  expires_at: discount.expires_at || '',
                                  is_active: discount.is_active
                                });
                                setShowDiscountModal(true);
                              }}
                              className="p-2 text-cyan-400 hover:bg-cyan-400/20 transition-all"
                              title="Edit"
                            >
                              <Edit className="w-4 h-4" />
                            </button>
                            <button
                              onClick={async () => {
                                if (window.confirm(`Delete discount code "${discount.code}"?`)) {
                                  try {
                                    await api.deleteDiscountCode(discount.id, storedPassword);
                                    setDiscountCodes(discountCodes.filter(d => d.id !== discount.id));
                                    const newStats = await api.getDiscountStats(storedPassword);
                                    setDiscountStats(newStats);
                                  } catch (error) {
                                    console.error('Error deleting discount:', error);
                                    alert('Failed to delete discount code');
                                  }
                                }
                              }}
                              className="p-2 text-red-400 hover:bg-red-500/20 transition-all"
                              title="Delete"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                        </div>
                        <div className="mt-3 grid grid-cols-3 gap-4 text-sm">
                          <div>
                            <p className="text-neutral-500 text-xs">Usage</p>
                            <p className="text-white">
                              {discount.usage_count || 0} {discount.usage_limit > 0 ? `/ ${discount.usage_limit}` : '(unlimited)'}
                            </p>
                          </div>
                          <div>
                            <p className="text-neutral-500 text-xs">Total Discounted</p>
                            <p className="text-green-400 font-mono">${(discount.total_discount_given || 0).toFixed(2)}</p>
                          </div>
                          <div>
                            <p className="text-neutral-500 text-xs">Customers Used</p>
                            <p className="text-white">{discount.used_by_emails?.length || 0}</p>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* BTO Orders Tab */}
            {activeTab === 'bto' && (
              <div className="space-y-6">
                {/* BTO Stats */}
                {btoStats && (
                  <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                    <div className="bg-void-paper border border-amber-500/20 p-4">
                      <p className="text-neutral-400 text-xs font-mono uppercase mb-1">Total BTO Orders</p>
                      <p className="text-2xl font-bold text-amber-500">{btoStats.total_orders}</p>
                    </div>
                    <div className="bg-void-paper border border-yellow-500/20 p-4">
                      <p className="text-neutral-400 text-xs font-mono uppercase mb-1">Pending</p>
                      <p className="text-2xl font-bold text-yellow-500">{btoStats.pending}</p>
                    </div>
                    <div className="bg-void-paper border border-blue-500/20 p-4">
                      <p className="text-neutral-400 text-xs font-mono uppercase mb-1">In Production</p>
                      <p className="text-2xl font-bold text-blue-500">{btoStats.in_production}</p>
                    </div>
                    <div className="bg-void-paper border border-green-500/20 p-4">
                      <p className="text-neutral-400 text-xs font-mono uppercase mb-1">Shipped</p>
                      <p className="text-2xl font-bold text-green-500">{btoStats.shipped}</p>
                    </div>
                    <div className="bg-void-paper border border-cyan-500/20 p-4">
                      <p className="text-neutral-400 text-xs font-mono uppercase mb-1">Revenue</p>
                      <p className="text-2xl font-bold text-cyan-400">${(btoStats.total_revenue || 0).toFixed(2)}</p>
                    </div>
                  </div>
                )}

                {/* BTO Orders List */}
                <div className="bg-void-paper border border-white/10">
                  <div className="p-4 border-b border-white/10 flex items-center gap-2">
                    <Hammer className="w-5 h-5 text-amber-500" />
                    <h3 className="font-mono text-sm uppercase tracking-widest text-white">BTO Orders Queue</h3>
                  </div>
                  
                  {btoOrders.length === 0 ? (
                    <div className="p-8 text-center text-neutral-400">
                      <Hammer className="w-12 h-12 mx-auto mb-4 opacity-50" />
                      <p>No BTO orders yet</p>
                    </div>
                  ) : (
                    <div className="divide-y divide-white/5">
                      {btoOrders.map((order) => (
                        <div key={order.id} className="p-4">
                          <div 
                            className="flex items-start justify-between cursor-pointer"
                            onClick={() => setExpandedBTOOrder(expandedBTOOrder === order.id ? null : order.id)}
                          >
                            <div className="flex-1">
                              <div className="flex items-center gap-3 mb-2">
                                <span className="font-mono text-amber-500 text-sm">#{order.id.slice(0, 8).toUpperCase()}</span>
                                <span className={`px-2 py-0.5 text-xs font-mono uppercase tracking-wider rounded ${
                                  order.status === 'pending' ? 'bg-yellow-500/20 text-yellow-500' :
                                  order.status === 'in_production' ? 'bg-blue-500/20 text-blue-500' :
                                  order.status === 'completed' ? 'bg-purple-500/20 text-purple-500' :
                                  order.status === 'shipped' ? 'bg-green-500/20 text-green-500' :
                                  order.status === 'cancelled' ? 'bg-red-500/20 text-red-500' :
                                  'bg-neutral-500/20 text-neutral-500'
                                }`}>
                                  {order.status?.replace('_', ' ')}
                                </span>
                              </div>
                              <p className="text-white font-medium">{order.item?.product_title}</p>
                              <p className="text-neutral-400 text-sm">
                                {order.customer_info?.first_name} {order.customer_info?.last_name} - {order.customer_info?.email}
                              </p>
                              <p className="text-neutral-500 text-xs mt-1">
                                {new Date(order.created_at).toLocaleDateString()} - Qty: {order.item?.quantity} - Total: ${order.total?.toFixed(2)}
                              </p>
                            </div>
                            <div className="flex items-center gap-2">
                              <span className="text-amber-500 font-mono font-bold">${order.total?.toFixed(2)}</span>
                              {expandedBTOOrder === order.id ? <ChevronUp className="w-4 h-4 text-neutral-400" /> : <ChevronDown className="w-4 h-4 text-neutral-400" />}
                            </div>
                          </div>

                          {/* Expanded Order Details */}
                          {expandedBTOOrder === order.id && (
                            <div className="mt-4 pt-4 border-t border-white/10 space-y-4" onClick={(e) => e.stopPropagation()}>
                              {/* Collapse Button */}
                              <div className="flex justify-end">
                                <button
                                  onClick={() => setExpandedBTOOrder(null)}
                                  className="text-neutral-400 hover:text-white text-xs font-mono uppercase tracking-wider flex items-center gap-1"
                                >
                                  <ChevronUp className="w-3 h-3" /> Collapse
                                </button>
                              </div>
                              {/* Customer Details */}
                              <div className="grid grid-cols-2 gap-4">
                                <div>
                                  <h4 className="text-xs text-neutral-500 uppercase tracking-wider mb-2">Customer Info</h4>
                                  <p className="text-white text-sm">{order.customer_info?.first_name} {order.customer_info?.last_name}</p>
                                  <p className="text-neutral-400 text-sm">{order.customer_info?.email}</p>
                                  {order.customer_info?.phone && <p className="text-neutral-400 text-sm">{order.customer_info.phone}</p>}
                                </div>
                                <div>
                                  <h4 className="text-xs text-neutral-500 uppercase tracking-wider mb-2">Shipping Address</h4>
                                  <p className="text-white text-sm">{order.customer_info?.address}</p>
                                  <p className="text-neutral-400 text-sm">
                                    {order.customer_info?.city}, {order.customer_info?.state} {order.customer_info?.zip_code}
                                  </p>
                                </div>
                              </div>

                              {/* Order Details */}
                              <div className="grid grid-cols-2 gap-4">
                                <div>
                                  <h4 className="text-xs text-neutral-500 uppercase tracking-wider mb-2">Order Details</h4>
                                  <p className="text-white text-sm">Product: {order.item?.product_title}</p>
                                  <p className="text-neutral-400 text-sm">Quantity: {order.item?.quantity}</p>
                                  <p className="text-neutral-400 text-sm">Subtotal: ${order.subtotal?.toFixed(2)}</p>
                                  <p className="text-neutral-400 text-sm">Shipping: ${order.shipping_cost?.toFixed(2)} ({order.shipping_method})</p>
                                </div>
                                <div>
                                  <h4 className="text-xs text-neutral-500 uppercase tracking-wider mb-2">Preferences</h4>
                                  <p className="text-neutral-400 text-sm">Color: {order.item?.color_preference || 'None specified'}</p>
                                  <p className="text-neutral-400 text-sm">Notes: {order.item?.special_notes || 'None'}</p>
                                </div>
                              </div>

                              {/* Admin Notes */}
                              <div>
                                <h4 className="text-xs text-neutral-500 uppercase tracking-wider mb-2">Admin Notes</h4>
                                <textarea
                                  value={order.notes || ''}
                                  onChange={async (e) => {
                                    const newNotes = e.target.value;
                                    setBtoOrders(btoOrders.map(o => o.id === order.id ? { ...o, notes: newNotes } : o));
                                  }}
                                  onBlur={async (e) => {
                                    try {
                                      await api.updateBTOOrder(order.id, { notes: e.target.value });
                                    } catch (error) {
                                      console.error('Error saving notes:', error);
                                    }
                                  }}
                                  placeholder="Add notes about this order..."
                                  rows={2}
                                  className="w-full bg-void border border-white/10 px-3 py-2 text-white text-sm placeholder:text-neutral-600 focus:border-amber-500/50 focus:outline-none resize-none"
                                />
                              </div>

                              {/* Status Update Buttons */}
                              <div>
                                <h4 className="text-xs text-neutral-500 uppercase tracking-wider mb-2">Update Status</h4>
                                <div className="flex flex-wrap gap-2">
                                  {['pending', 'in_production', 'completed', 'shipped', 'cancelled'].map((status) => (
                                    <button
                                      key={status}
                                      onClick={async () => {
                                        try {
                                          await api.updateBTOOrder(order.id, { status });
                                          setBtoOrders(btoOrders.map(o => o.id === order.id ? { ...o, status } : o));
                                          const newStats = await api.getBTOStats();
                                          setBtoStats(newStats);
                                        } catch (error) {
                                          console.error('Error updating status:', error);
                                          alert('Failed to update status');
                                        }
                                      }}
                                      className={`px-3 py-1.5 text-xs font-mono uppercase tracking-wider transition-all ${
                                        order.status === status
                                          ? status === 'pending' ? 'bg-yellow-500 text-black' :
                                            status === 'in_production' ? 'bg-blue-500 text-white' :
                                            status === 'completed' ? 'bg-purple-500 text-white' :
                                            status === 'shipped' ? 'bg-green-500 text-white' :
                                            'bg-red-500 text-white'
                                          : 'bg-void border border-white/20 text-neutral-400 hover:text-white hover:border-white/40'
                                      }`}
                                    >
                                      {status === 'pending' && <Clock className="w-3 h-3 inline mr-1" />}
                                      {status === 'in_production' && <Hammer className="w-3 h-3 inline mr-1" />}
                                      {status === 'completed' && <CheckCircle className="w-3 h-3 inline mr-1" />}
                                      {status === 'shipped' && <TruckIcon className="w-3 h-3 inline mr-1" />}
                                      {status === 'cancelled' && <X className="w-3 h-3 inline mr-1" />}
                                      {status.replace('_', ' ')}
                                    </button>
                                  ))}
                                </div>
                              </div>

                              {/* Delete Button */}
                              <div className="pt-2 border-t border-white/10">
                                <button
                                  onClick={async () => {
                                    if (window.confirm('Are you sure you want to delete this BTO order?')) {
                                      try {
                                        await api.deleteBTOOrder(order.id);
                                        setBtoOrders(btoOrders.filter(o => o.id !== order.id));
                                        const newStats = await api.getBTOStats();
                                        setBtoStats(newStats);
                                      } catch (error) {
                                        console.error('Error deleting order:', error);
                                        alert('Failed to delete order');
                                      }
                                    }
                                  }}
                                  className="text-red-400 hover:text-red-300 text-xs font-mono uppercase tracking-wider"
                                >
                                  <Trash2 className="w-3 h-3 inline mr-1" />
                                  Delete Order
                                </button>
                              </div>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Quick Links Tab */}
            {activeTab === 'links' && (
              <div className="space-y-6">
                <div className="bg-void-paper border border-white/10 p-6">
                  <div className="flex items-center gap-3 mb-6">
                    <Link2 className="w-6 h-6 text-cyan-400" />
                    <h3 className="font-mono text-lg uppercase tracking-widest text-white">Quick Links & QR Codes</h3>
                  </div>
                  
                  <p className="text-neutral-400 text-sm mb-8">
                    Secret links for sharing with partners and customers. These pages are not visible in the main navigation.
                  </p>

                  {/* Retail Partner Link */}
                  <div className="border border-amber-500/30 bg-amber-500/5 p-6 mb-6">
                    <div className="flex flex-col lg:flex-row gap-6">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-3">
                          <QrCode className="w-5 h-5 text-amber-500" />
                          <h4 className="font-mono text-sm uppercase tracking-widest text-amber-500 font-bold">Retail Partnership Program</h4>
                        </div>
                        <p className="text-neutral-400 text-sm mb-4">
                          Send this link to potential wholesale clients to show consignment vs wholesale options.
                        </p>
                        
                        <div className="bg-void border border-white/10 p-3 flex items-center justify-between gap-3 mb-4">
                          <code className="text-cyan-400 text-sm break-all">
                            {window.location.origin}/retailpartnership
                          </code>
                          <button
                            onClick={() => {
                              navigator.clipboard.writeText(`${window.location.origin}/retailpartnership`);
                              setCopiedLink('retailpartnership');
                              setTimeout(() => setCopiedLink(null), 2000);
                            }}
                            className="flex-shrink-0 px-3 py-1.5 bg-cyan-400 text-black text-xs font-mono uppercase tracking-wider hover:bg-cyan-300 transition-colors flex items-center gap-1"
                          >
                            {copiedLink === 'retailpartnership' ? <><Check className="w-3 h-3" /> Copied!</> : <><Copy className="w-3 h-3" /> Copy</>}
                          </button>
                        </div>
                        
                        <a 
                          href="/retailpartnership" 
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-2 text-amber-500 hover:text-amber-400 text-sm font-mono uppercase tracking-wider"
                        >
                          <ExternalLink className="w-4 h-4" /> Open Page
                        </a>
                      </div>
                      
                      <div className="flex flex-col items-center">
                        <p className="text-neutral-500 text-xs uppercase tracking-wider mb-3">Scan QR Code</p>
                        <div className="bg-white p-3 rounded">
                          <img 
                            src={`https://api.qrserver.com/v1/create-qr-code/?size=150x150&data=${encodeURIComponent(window.location.origin + '/retailpartnership')}`}
                            alt="Retail Partner QR Code"
                            className="w-36 h-36"
                          />
                        </div>
                        <button
                          onClick={() => {
                            const link = document.createElement('a');
                            link.href = `https://api.qrserver.com/v1/create-qr-code/?size=300x300&data=${encodeURIComponent(window.location.origin + '/retailpartnership')}`;
                            link.download = 'onlyskullz-retailpartnership-qr.png';
                            link.click();
                          }}
                          className="mt-3 px-4 py-2 bg-void border border-amber-500/30 text-amber-500 text-xs font-mono uppercase tracking-wider hover:bg-amber-500/10 transition-colors flex items-center gap-2"
                        >
                          <Download className="w-3 h-3" /> Download QR
                        </button>
                      </div>
                    </div>
                  </div>

                  {/* Admin Link */}
                  <div className="border border-white/10 bg-void p-6">
                    <div className="flex items-center gap-2 mb-3">
                      <Lock className="w-5 h-5 text-cyan-400" />
                      <h4 className="font-mono text-sm uppercase tracking-widest text-white font-bold">Admin Dashboard</h4>
                    </div>
                    <p className="text-neutral-400 text-sm mb-4">
                      Direct link to this admin panel (password protected).
                    </p>
                    
                    <div className="bg-void-paper border border-white/10 p-3 flex items-center justify-between gap-3">
                      <code className="text-cyan-400 text-sm break-all">
                        {window.location.origin}/admin
                      </code>
                      <button
                        onClick={() => {
                          navigator.clipboard.writeText(`${window.location.origin}/admin`);
                          setCopiedLink('admin');
                          setTimeout(() => setCopiedLink(null), 2000);
                        }}
                        className="flex-shrink-0 px-3 py-1.5 bg-cyan-400 text-black text-xs font-mono uppercase tracking-wider hover:bg-cyan-300 transition-colors flex items-center gap-1"
                      >
                        {copiedLink === 'admin' ? <><Check className="w-3 h-3" /> Copied!</> : <><Copy className="w-3 h-3" /> Copy</>}
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* Gallery Photo Modal - Add/Edit */}
      {showGalleryModal && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
          <div className="bg-void-paper border border-white/10 p-6 max-w-lg w-full max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-bold text-white">
                {editingGalleryPhoto ? 'Edit Gallery Photo' : 'Add Gallery Photo'}
              </h2>
              <button onClick={() => { setShowGalleryModal(false); setGalleryForm({ url: '', caption: '' }); setEditingGalleryPhoto(null); }} className="text-neutral-400 hover:text-white">
                <X className="w-6 h-6" />
              </button>
            </div>
            <form onSubmit={async (e) => {
              e.preventDefault();
              if (!galleryForm.url) {
                alert('Please upload or enter an image URL');
                return;
              }
              try {
                if (editingGalleryPhoto) {
                  // Update existing photo caption
                  const updated = await api.updateGalleryPhoto(editingGalleryPhoto.id, { caption: galleryForm.caption }, storedPassword);
                  setGalleryPhotos(galleryPhotos.map(p => p.id === editingGalleryPhoto.id ? updated : p));
                } else {
                  // Add new photo
                  const newPhoto = await api.addGalleryPhoto(galleryForm, storedPassword);
                  setGalleryPhotos([newPhoto, ...galleryPhotos]);
                }
                setShowGalleryModal(false);
                setGalleryForm({ url: '', caption: '' });
                setEditingGalleryPhoto(null);
              } catch (error) {
                console.error('Error saving photo:', error);
                alert('Failed to save photo');
              }
            }} className="space-y-4">
              <div>
                <label className="block text-sm text-neutral-400 mb-2">Photo *</label>
                {galleryForm.url ? (
                  <div className="relative">
                    <img src={galleryForm.url} alt="Preview" className="w-full aspect-video object-cover border border-white/10" />
                    {!editingGalleryPhoto && (
                      <button type="button" onClick={() => setGalleryForm({ ...galleryForm, url: '' })} className="absolute top-2 right-2 p-1 bg-red-500 text-white rounded">
                        <X className="w-4 h-4" />
                      </button>
                    )}
                    {editingGalleryPhoto && (
                      <p className="mt-2 text-xs text-neutral-500">Photo cannot be changed. Delete and re-upload if you need a different image.</p>
                    )}
                  </div>
                ) : (
                  <div className="border-2 border-dashed border-white/20 p-8 text-center">
                    <input
                      type="file"
                      accept="image/*"
                      onChange={(e) => {
                        const file = e.target.files[0];
                        if (file) {
                          handleImageUpload(file, (url) => setGalleryForm({ ...galleryForm, url }));
                        }
                      }}
                      className="hidden"
                      id="gallery-upload"
                    />
                    <label htmlFor="gallery-upload" className="cursor-pointer">
                      {uploading ? (
                        <div className="flex items-center justify-center gap-2 text-cyan-400">
                          <Loader2 className="w-5 h-5 animate-spin" />
                          <span>Uploading...</span>
                        </div>
                      ) : (
                        <>
                          <Upload className="w-8 h-8 text-neutral-400 mx-auto mb-2" />
                          <p className="text-neutral-400 text-sm">Click to upload photo</p>
                        </>
                      )}
                    </label>
                  </div>
                )}
              </div>
              <div>
                <label className="block text-sm text-neutral-400 mb-2">Caption (optional)</label>
                <input
                  type="text"
                  value={galleryForm.caption}
                  onChange={(e) => setGalleryForm({ ...galleryForm, caption: e.target.value })}
                  placeholder="Add a caption..."
                  className="w-full bg-void border border-white/10 p-3 text-white"
                />
              </div>
              <button type="submit" className="w-full bg-cyan-400 text-black font-mono uppercase tracking-widest py-3 hover:bg-cyan-300 transition-all flex items-center justify-center gap-2">
                <Save className="w-4 h-4" /> {editingGalleryPhoto ? 'Update Caption' : 'Add Photo'}
              </button>
            </form>
          </div>
        </div>
      )}

      {/* Discount Code Modal - Add/Edit */}
      {showDiscountModal && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
          <div className="bg-void-paper border border-white/10 p-6 max-w-md w-full">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-bold text-white">
                {editingDiscount ? 'Edit Discount Code' : 'Create Discount Code'}
              </h2>
              <button 
                onClick={() => { 
                  setShowDiscountModal(false); 
                  setDiscountForm({ code: '', discount_type: 'percentage', discount_value: '', usage_limit: '', expires_at: '', is_active: true }); 
                  setEditingDiscount(null); 
                }} 
                className="text-neutral-400 hover:text-white"
              >
                <X className="w-6 h-6" />
              </button>
            </div>
            <form onSubmit={async (e) => {
              e.preventDefault();
              if (!discountForm.code || !discountForm.discount_value) {
                alert('Please fill in all required fields');
                return;
              }
              try {
                const codeData = {
                  code: discountForm.code.toUpperCase(),
                  discount_type: discountForm.discount_type,
                  discount_value: parseFloat(discountForm.discount_value),
                  usage_limit: discountForm.usage_limit ? parseInt(discountForm.usage_limit) : 0,
                  expires_at: discountForm.expires_at || null,
                  is_active: discountForm.is_active
                };
                
                if (editingDiscount) {
                  const updated = await api.updateDiscountCode(editingDiscount.id, codeData, storedPassword);
                  setDiscountCodes(discountCodes.map(d => d.id === editingDiscount.id ? updated : d));
                } else {
                  const newCode = await api.createDiscountCode(codeData, storedPassword);
                  setDiscountCodes([newCode, ...discountCodes]);
                }
                const newStats = await api.getDiscountStats(storedPassword);
                setDiscountStats(newStats);
                setShowDiscountModal(false);
                setDiscountForm({ code: '', discount_type: 'percentage', discount_value: '', usage_limit: '', expires_at: '', is_active: true });
                setEditingDiscount(null);
              } catch (error) {
                console.error('Error saving discount:', error);
                alert(error.response?.data?.detail || 'Failed to save discount code');
              }
            }} className="space-y-4">
              <div>
                <label className="block text-sm text-neutral-400 mb-2">Code *</label>
                <input
                  type="text"
                  value={discountForm.code}
                  onChange={(e) => setDiscountForm({ ...discountForm, code: e.target.value.toUpperCase() })}
                  placeholder="e.g., SAVE10, WELCOME20"
                  className="w-full bg-void border border-white/10 p-3 text-white font-mono uppercase"
                  data-testid="discount-code-input"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm text-neutral-400 mb-2">Type *</label>
                  <select
                    value={discountForm.discount_type}
                    onChange={(e) => setDiscountForm({ ...discountForm, discount_type: e.target.value })}
                    className="w-full bg-void border border-white/10 p-3 text-white"
                  >
                    <option value="percentage">Percentage (%)</option>
                    <option value="fixed">Fixed Amount ($)</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm text-neutral-400 mb-2">Value *</label>
                  <input
                    type="number"
                    step="0.01"
                    value={discountForm.discount_value}
                    onChange={(e) => setDiscountForm({ ...discountForm, discount_value: e.target.value })}
                    placeholder={discountForm.discount_type === 'percentage' ? '10' : '5.00'}
                    className="w-full bg-void border border-white/10 p-3 text-white"
                    data-testid="discount-value-input"
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm text-neutral-400 mb-2">Usage Limit (0 = unlimited)</label>
                <input
                  type="number"
                  value={discountForm.usage_limit}
                  onChange={(e) => setDiscountForm({ ...discountForm, usage_limit: e.target.value })}
                  placeholder="100"
                  className="w-full bg-void border border-white/10 p-3 text-white"
                />
                <p className="text-xs text-neutral-500 mt-1">Each customer can only use a code once regardless of this limit</p>
              </div>
              <div>
                <label className="block text-sm text-neutral-400 mb-2 flex items-center gap-2">
                  <Calendar className="w-3 h-3" />
                  Expiration Date (optional)
                </label>
                <input
                  type="date"
                  value={discountForm.expires_at}
                  onChange={(e) => setDiscountForm({ ...discountForm, expires_at: e.target.value })}
                  className="w-full bg-void border border-white/10 p-3 text-white"
                  min={new Date().toISOString().split('T')[0]}
                />
                <p className="text-xs text-neutral-500 mt-1">Leave empty for no expiration</p>
              </div>
              <div className="flex items-center gap-3">
                <input
                  type="checkbox"
                  id="discount-active"
                  checked={discountForm.is_active}
                  onChange={(e) => setDiscountForm({ ...discountForm, is_active: e.target.checked })}
                  className="w-4 h-4"
                />
                <label htmlFor="discount-active" className="text-neutral-400 text-sm">Active (customers can use this code)</label>
              </div>
              <button 
                type="submit" 
                className="w-full bg-cyan-400 text-black font-mono uppercase tracking-widest py-3 hover:bg-cyan-300 transition-all flex items-center justify-center gap-2"
                data-testid="save-discount-btn"
              >
                <Save className="w-4 h-4" /> {editingDiscount ? 'Update Code' : 'Create Code'}
              </button>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminPage;
